import React, { useState } from 'react';
import { motion } from 'motion/react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { AthleteProfile, Badge, SportType, AppNotification, HighlightClip } from '../types';
import { StreakCalendar } from './StreakCalendar';
import { VolleyballChallengesView } from './VolleyballChallengesView';
import { HighlightReelView } from './HighlightReelView';
import { ConfettiExplosion } from './ConfettiExplosion';
import { getLevelInfo, getCumulativeXpForLevel, ELITE_CHALLENGES, EliteChallenge } from '../utils/levelUtils';
import { claimAchievementXP } from '../utils/achievementXPIntegration';
import { getLegendaryBadgeFilter, fireEliteSeriesConfetti } from '../utils/badgeEvaluator';
import { saveNotificationToFirestore, saveAthleteToFirestore } from '../services/firestoreService';
import { 
  Trophy, 
  Flame, 
  Award, 
  ShieldCheck, 
  Crown, 
  Target, 
  Users, 
  Film, 
  Zap, 
  Star, 
  Sparkles, 
  MapPin, 
  Check, 
  Lock, 
  Search, 
  Filter,
  CheckCircle2,
  ChevronRight,
  Medal,
  Dumbbell,
  Activity,
  Share2,
  Download,
  Copy,
  X,
  Image as ImageIcon,
  Pin,
  Play,
  Video,
  Plus,
  Eye,
  Heart,
  Square
} from 'lucide-react';

interface AchievementsProps {
  user: AthleteProfile;
  allAthletes?: AthleteProfile[];
  onClaimXp?: (amount: number, badgeName: string) => void;
  onUpdateProfile?: (updated: Partial<AthleteProfile>) => void;
  activePalette?: string;
  initialSubTab?: 'showcase' | 'achievements' | 'reels' | 'challenges';
  onOpenUploadModal?: () => void;
}

export interface MilestoneItem {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: SportType | 'general' | 'elite_series';
  tier: 'bronze' | 'silver' | 'gold' | 'platinum' | 'diamond' | 'pro_elite' | 'legendary';
  targetValue: number;
  currentValue: number;
  xpReward: number;
  unlockedAt?: string;
  tips?: string;
  isProVerified?: boolean;
}

/* VOLLEYBALL WEEKLY CHALLENGES & DRILLS MODULE */
const VolleyballWeeklyChallengesSection: React.FC<{
  user: AthleteProfile;
  onClaimXp?: (amount: number, badgeName: string) => void;
  onUpdateProfile?: (updated: Partial<AthleteProfile>) => void;
}> = ({ user, onClaimXp, onUpdateProfile }) => {
  const [drills, setDrills] = useState([
    {
      id: 'drill_perfect_set',
      title: 'Perfect Set Drill 🎯',
      description: 'Record 30 precision setting assists in volleyball runs or practice.',
      target: 30,
      current: 18,
      rewardXp: 250,
      unit: 'Assists',
      claimed: false,
    },
    {
      id: 'drill_service_streak',
      title: 'Service Streak Challenge ⚡',
      description: 'Serve 10 unreturnable aces in weekly volleyball matches.',
      target: 10,
      current: 7,
      rewardXp: 300,
      unit: 'Aces',
      claimed: false,
    },
    {
      id: 'drill_dig_marathon',
      title: 'Backcourt Dig Marathon 🛡️',
      description: 'Log 40 floor digs saving hard spike hits in weekly games.',
      target: 40,
      current: 28,
      rewardXp: 350,
      unit: 'Digs',
      claimed: false,
    },
    {
      id: 'drill_net_defense',
      title: 'Net Wall Defense 🧱',
      description: 'Touch or stuff 15 opponent spike attempts at the net.',
      target: 15,
      current: 11,
      rewardXp: 200,
      unit: 'Blocks',
      claimed: false,
    },
  ]);

  const [toastMsg, setToastMsg] = useState<string | null>(null);

  const handlePracticeDrill = (drillId: string, incrementAmount: number) => {
    setDrills((prev) =>
      prev.map((d) => {
        if (d.id === drillId) {
          const nextVal = Math.min(d.target, d.current + incrementAmount);
          return { ...d, current: nextVal };
        }
        return d;
      })
    );
    setToastMsg('Drill practice logged! Progress updated towards weekly bonus XP.');
    setTimeout(() => setToastMsg(null), 2500);
  };

  const handleClaimDrill = (drillId: string, rewardXp: number, title: string) => {
    setDrills((prev) =>
      prev.map((d) => (d.id === drillId ? { ...d, claimed: true } : d))
    );
    const result = claimAchievementXP(user, rewardXp, title);
    if (onUpdateProfile) onUpdateProfile(result.updatedUser);
    if (onClaimXp) onClaimXp(rewardXp, title);
    setToastMsg(`Claimed +${rewardXp} XP for completing ${title}!`);
    setTimeout(() => setToastMsg(null), 2500);
  };

  return (
    <div className="bg-gradient-to-br from-purple-950 via-indigo-950 to-indigo-900 p-6 rounded-[2.5rem] border-2 border-lime-400/40 shadow-2xl space-y-5">
      {toastMsg && (
        <div className="p-3 bg-lime-400 text-black font-black text-xs uppercase rounded-xl shadow-lg border border-lime-300 font-mono flex items-center justify-between animate-fadeIn">
          <span>✨ {toastMsg}</span>
          <button onClick={() => setToastMsg(null)} className="text-black font-bold">✕</button>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-white/10 pb-4">
        <div className="flex items-center space-x-3">
          <div className="p-3 bg-lime-400 text-black rounded-2xl shadow-lg">
            <Target className="w-6 h-6 stroke-[2.5]" />
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <span className="px-2 py-0.5 bg-lime-400 text-black font-mono font-black text-[10px] uppercase rounded">
                WEEKLY DRILLS & CHALLENGES
              </span>
              <span className="text-xs text-indigo-200 font-bold">Volleyball League 🏐</span>
            </div>
            <h3 className="text-xl font-black italic uppercase text-white mt-0.5">
              Volleyball Weekly Challenges & Bonus XP
            </h3>
          </div>
        </div>

        <div className="flex items-center space-x-2 bg-indigo-950/90 px-3.5 py-1.5 rounded-2xl border border-white/10 text-xs text-indigo-200 font-mono shrink-0">
          <span>Resets in:</span>
          <span className="text-lime-400 font-black">3d 14h</span>
        </div>
      </div>

      {/* Grid of 4 Drills */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {drills.map((d) => {
          const isComplete = d.current >= d.target;

          return (
            <div
              key={d.id}
              className={`p-4 rounded-2xl border transition ${
                d.claimed
                  ? 'bg-indigo-950/40 border-lime-400/20 opacity-75'
                  : isComplete
                  ? 'bg-indigo-950 border-lime-400 shadow-lg shadow-lime-400/10'
                  : 'bg-indigo-950/80 border-white/10'
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h4 className="font-extrabold text-sm text-white flex items-center space-x-1.5">
                    <span>{d.title}</span>
                  </h4>
                  <p className="text-xs text-indigo-200/80 mt-1">{d.description}</p>
                </div>

                <span className="px-2.5 py-1 bg-lime-400/20 border border-lime-400/40 text-lime-300 font-mono font-black text-xs rounded-xl shrink-0">
                  +{d.rewardXp} XP
                </span>
              </div>

              {/* Progress bar */}
              <div className="mt-3 space-y-1.5">
                <div className="flex justify-between text-[11px] font-mono">
                  <span className="text-indigo-300 font-bold">
                    Progress: {d.current} / {d.target} {d.unit}
                  </span>
                  <span className={isComplete ? 'text-lime-400 font-black' : 'text-indigo-300/80'}>
                    {Math.round((d.current / d.target) * 100)}%
                  </span>
                </div>
                <div className="w-full bg-indigo-900 h-2.5 rounded-full overflow-hidden border border-white/10">
                  <div
                    className={`h-full rounded-full transition-all duration-300 ${
                      isComplete ? 'bg-lime-400' : 'bg-cyan-400'
                    }`}
                    style={{ width: `${Math.min(100, (d.current / d.target) * 100)}%` }}
                  />
                </div>
              </div>

              {/* Interactive Actions */}
              <div className="mt-3.5 flex items-center justify-between pt-2 border-t border-white/10">
                {!isComplete ? (
                  <button
                    type="button"
                    onClick={() => handlePracticeDrill(d.id, 3)}
                    className="px-3 py-1.5 bg-indigo-900 hover:bg-indigo-800 text-indigo-100 font-bold text-xs rounded-xl border border-white/10 transition flex items-center space-x-1"
                  >
                    <Zap className="w-3.5 h-3.5 text-lime-400" />
                    <span>Practice Drill (+3 {d.unit})</span>
                  </button>
                ) : d.claimed ? (
                  <span className="text-xs font-black text-lime-400 font-mono uppercase flex items-center space-x-1">
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Reward Claimed</span>
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleClaimDrill(d.id, d.rewardXp, d.title)}
                    className="px-4 py-1.5 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase text-xs rounded-xl shadow-lg transition flex items-center space-x-1 animate-pulse"
                  >
                    <Trophy className="w-3.5 h-3.5" />
                    <span>Claim +{d.rewardXp} XP</span>
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const Achievements: React.FC<AchievementsProps> = ({ 
  user, 
  allAthletes = [], 
  onClaimXp, 
  onUpdateProfile, 
  activePalette,
  initialSubTab = 'showcase',
  onOpenUploadModal
}) => {
  const [activeHubTab, setActiveHubTab] = useState<'showcase' | 'achievements' | 'reels' | 'challenges'>(initialSubTab || 'showcase');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'unlocked' | 'locked'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMilestone, setSelectedMilestone] = useState<MilestoneItem | null>(null);
  const [claimedBadges, setClaimedBadges] = useState<Record<string, boolean>>({});

  // Pinned Badges State for Highlight Reel Featured Showcase
  const userSport = user.primarySport || 'volleyball';

  const defaultPinnedBySport: Record<string, string[]> = {
    volleyball: ['m_vb_spike_cannon', 'm_vb_ace_master', 'm_vb_net_guardian', 'm_rising_star', 'm_first_win'],
    basketball: ['m_100_pts', 'm_50_reb', 'm_25_ast', 'm_rising_star'],
    football: ['m_century_scorer', 'm_first_win', 'm_25_games', 'm_rising_star'],
    soccer: ['m_century_scorer', 'm_dime_dropper', 'm_first_win', 'm_25_games'],
  };

  const [pinnedBadgeIds, setPinnedBadgeIds] = useState<string[]>(
    user.pinnedBadgeIds || defaultPinnedBySport[userSport] || defaultPinnedBySport.volleyball
  );
  const [showcaseToastMsg, setShowcaseToastMsg] = useState<string | null>(null);

  // Profile Showcase Rarity Filter State
  const [showcaseRarityFilter, setShowcaseRarityFilter] = useState<'all' | 'bronze' | 'silver' | 'gold' | 'platinum' | 'diamond'>('all');

  // Confetti Explosion state for Legendary rank & milestone unlocks
  const [confettiConfig, setConfettiConfig] = useState<{
    isVisible: boolean;
    tier?: 'standard' | 'legendary';
    title?: string;
    subtitle?: string;
  }>({ isVisible: false, tier: 'standard' });

  // Share Achievement Snapshot State
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [shareMilestone, setShareMilestone] = useState<MilestoneItem | null>(null);
  const [snapshotToastMsg, setSnapshotToastMsg] = useState<string | null>(null);

  // Quick Preview Modal & Multi-Select Batch Actions State
  const [selectedBadgeIds, setSelectedBadgeIds] = useState<string[]>([]);
  const [previewBadge, setPreviewBadge] = useState<MilestoneItem | null>(null);

  const handleToggleSelectBadge = (badgeId: string) => {
    setSelectedBadgeIds((prev) =>
      prev.includes(badgeId) ? prev.filter((id) => id !== badgeId) : [...prev, badgeId]
    );
  };

  const handleDeselectAllBadges = () => {
    setSelectedBadgeIds([]);
  };

  const handleBatchPinToFeatured = () => {
    if (selectedBadgeIds.length === 0) return;
    const newPinned = Array.from(new Set([...pinnedBadgeIds, ...selectedBadgeIds]));
    setPinnedBadgeIds(newPinned);
    if (onUpdateProfile) {
      onUpdateProfile({ pinnedBadgeIds: newPinned });
    }
    saveAthleteToFirestore({ ...user, pinnedBadgeIds: newPinned });
    setShowcaseToastMsg(`📌 Pinned ${selectedBadgeIds.length} selected badge${selectedBadgeIds.length > 1 ? 's' : ''} to your Profile Showcase!`);
    setTimeout(() => setShowcaseToastMsg(null), 3500);
    setSelectedBadgeIds([]);
  };

  const handleBatchShareSelection = () => {
    if (selectedBadgeIds.length === 0) return;
    setShowcaseToastMsg(`✨ Shared ${selectedBadgeIds.length} selected badge${selectedBadgeIds.length > 1 ? 's' : ''} to your social feed!`);
    setTimeout(() => setShowcaseToastMsg(null), 3500);
    setSelectedBadgeIds([]);
  };

  // Compute stats metrics from user profile
  const totalGamesPlayed = 
    (user.stats?.basketball?.gamesPlayed || 0) +
    (user.stats?.baseball?.gamesPlayed || 0) +
    (user.stats?.softball?.gamesPlayed || 0) +
    (user.stats?.pickleball?.gamesPlayed || 0) +
    (user.stats?.soccer?.gamesPlayed || 0) +
    (user.stats?.volleyball?.gamesPlayed || 0) +
    (user.stats?.football?.gamesPlayed || 0) +
    (user.stats?.tennis?.gamesPlayed || 0);

  const totalPointsScored = 
    (user.stats?.basketball?.pts || 0) +
    (user.stats?.soccer?.goals * 2 || 0) +
    (user.stats?.football?.passingYards / 20 || 0);

  const totalAssists = 
    (user.stats?.basketball?.ast || 0) +
    (user.stats?.soccer?.assists || 0);

  const totalRebounds = 
    (user.stats?.basketball?.reb || 0) +
    (user.stats?.volleyball?.kills || 0);

  const totalHighlightsCount = user.highlights?.length || 0;

  const activeSportsCount = [
    user.stats?.basketball?.gamesPlayed,
    user.stats?.baseball?.gamesPlayed,
    user.stats?.softball?.gamesPlayed,
    user.stats?.pickleball?.gamesPlayed,
    user.stats?.soccer?.gamesPlayed,
    user.stats?.volleyball?.gamesPlayed,
    user.stats?.football?.gamesPlayed,
    user.stats?.tennis?.gamesPlayed,
  ].filter((g) => g && g > 0).length;

  // Master Milestones Catalog with live calculated values
  const milestonesList: MilestoneItem[] = [
    {
      id: 'm_first_win',
      name: 'First Win',
      description: 'Record your first official victory on the playground courts.',
      icon: 'Trophy',
      category: 'general',
      tier: 'bronze',
      targetValue: 1,
      currentValue: user.winCount || 0,
      xpReward: 100,
      unlockedAt: user.winCount >= 1 ? '2026-07-15' : undefined,
      tips: 'Join or host a pickup match and secure a victory with your team.',
    },
    {
      id: 'm_10_games',
      name: '10 Games Played',
      description: 'Log 10 total pickup games across any sport.',
      icon: 'Activity',
      category: 'general',
      tier: 'silver',
      targetValue: 10,
      currentValue: totalGamesPlayed,
      xpReward: 250,
      unlockedAt: totalGamesPlayed >= 10 ? '2026-07-22' : undefined,
      tips: 'Keep logging your match stats after pickup runs at local parks.',
    },
    {
      id: 'm_25_games',
      name: '25 Games Veteran',
      description: 'Log 25 total pickup games on the platform.',
      icon: 'Crown',
      category: 'general',
      tier: 'gold',
      targetValue: 25,
      currentValue: totalGamesPlayed,
      xpReward: 500,
      unlockedAt: totalGamesPlayed >= 25 ? '2026-07-28' : undefined,
      tips: 'Play consistently every week across playground venues.',
    },
    {
      id: 'm_highlight_debut',
      name: 'Highlight Reel Debut',
      description: 'Record or upload your first 30-second game highlight reel.',
      icon: 'Film',
      category: 'general',
      tier: 'bronze',
      targetValue: 1,
      currentValue: totalHighlightsCount,
      xpReward: 150,
      unlockedAt: totalHighlightsCount >= 1 ? '2026-07-20' : undefined,
      tips: 'Use the "Add Highlight" button on your profile or upload video clips.',
    },
    {
      id: 'm_century_scorer',
      name: 'Century Scorer',
      description: 'Reach 50+ total accumulated points across basketball, soccer, or football.',
      icon: 'Flame',
      category: 'basketball',
      tier: 'gold',
      targetValue: 50,
      currentValue: Math.floor(totalPointsScored),
      xpReward: 350,
      unlockedAt: totalPointsScored >= 50 ? '2026-07-25' : undefined,
      tips: 'Log game stats including points, goals, and passing touchdowns.',
    },
    {
      id: 'm_dime_dropper',
      name: 'Dime Dropper',
      description: 'Dish out 10 total assists across basketball or soccer matches.',
      icon: 'Zap',
      category: 'basketball',
      tier: 'silver',
      targetValue: 10,
      currentValue: totalAssists,
      xpReward: 200,
      unlockedAt: totalAssists >= 10 ? '2026-07-26' : undefined,
      tips: 'Set up your teammates with key passes during pickup runs.',
    },
    {
      id: 'm_iron_shield',
      name: 'Iron Shield Security',
      description: 'Enable Multi-Factor Authentication (MFA) to protect your athlete account.',
      icon: 'ShieldCheck',
      category: 'general',
      tier: 'bronze',
      targetValue: 1,
      currentValue: user.mfaEnabled ? 1 : 0,
      xpReward: 150,
      unlockedAt: user.mfaEnabled ? '2026-07-18' : undefined,
      tips: 'Toggle "Enable MFA" under Profile Security settings.',
    },
    {
      id: 'm_multi_sport',
      name: 'Multi-Sport Dynamo',
      description: 'Log games in at least 2 different sports (Basketball, Baseball, Pickleball, Soccer, etc.).',
      icon: 'Dumbbell',
      category: 'general',
      tier: 'silver',
      targetValue: 2,
      currentValue: activeSportsCount,
      xpReward: 300,
      unlockedAt: activeSportsCount >= 2 ? '2026-07-29' : undefined,
      tips: 'Branch out and try a pickleball match or baseball diamond run.',
    },
    {
      id: 'm_court_explorer',
      name: 'Court Explorer',
      description: 'Check in or play games at 5 different sports parks or venues.',
      icon: 'MapPin',
      category: 'general',
      tier: 'silver',
      targetValue: 5,
      currentValue: user.id === 'user_1' ? 5 : Math.min(totalGamesPlayed, 5),
      xpReward: 200,
      unlockedAt: user.id === 'user_1' || totalGamesPlayed >= 5 ? (user.id === 'user_1' ? '2026-07-28' : 'Just now') : undefined,
      tips: 'Explore courts on the interactive Park Map view.',
    },
    {
      id: 'm_captain_marvel',
      name: 'Captain Host',
      description: 'Host a pickup game and send email/SMS invites to players.',
      icon: 'Users',
      category: 'general',
      tier: 'gold',
      targetValue: 1,
      currentValue: user.id === 'user_1' ? 1 : 0,
      xpReward: 300,
      unlockedAt: user.id === 'user_1' ? '2026-07-30' : undefined,
      tips: 'Use the "Host Game" feature and invite teammates via email or text.',
    },
    {
      id: 'm_rising_star',
      name: 'Rising Star Level',
      description: 'Reach Athlete Level 2 or higher.',
      icon: 'Star',
      category: 'general',
      tier: 'platinum',
      targetValue: 2,
      currentValue: user.level,
      xpReward: 400,
      unlockedAt: user.level >= 2 ? '2026-07-24' : undefined,
      tips: 'Earn XP by completing daily goals and logging stats.',
    },
    {
      id: 'm_hall_of_famer',
      name: 'Hall of Fame Status',
      description: 'Reach Athlete Level 5 and earn Hall of Famer title.',
      icon: 'Medal',
      category: 'general',
      tier: 'diamond',
      targetValue: 5,
      currentValue: user.level,
      xpReward: 1000,
      unlockedAt: user.level >= 5 ? '2026-07-31' : undefined,
      tips: 'Climb the global leaderboards to achieve Hall of Fame status.',
    },
    {
      id: 'm_streak_master',
      name: '7-Day Streak Master',
      description: 'Log in and check in for 7 consecutive calendar days.',
      icon: 'Flame',
      category: 'general',
      tier: 'gold',
      targetValue: 7,
      currentValue: user.dailyStreak || 1,
      xpReward: 350,
      unlockedAt: (user.dailyStreak || 1) >= 7 ? '2026-08-01' : undefined,
      tips: 'Use the Daily Check-in reward button on your dashboard every day.',
    },
    {
      id: 'm_streak_shield_owner',
      name: 'Streak Shield Guardian',
      description: 'Hold at least 1 Streak Freeze token in your inventory.',
      icon: 'ShieldCheck',
      category: 'general',
      tier: 'silver',
      targetValue: 1,
      currentValue: user.streakFreezeCount || 0,
      xpReward: 200,
      unlockedAt: (user.streakFreezeCount || 0) >= 1 ? '2026-08-05' : undefined,
      tips: 'Purchase a Streak Freeze with 250 XP to protect your streak against missed days.',
    },
    {
      id: 'm_100_pts',
      name: '100 Career Points Threshold',
      description: 'Reach 100 total career points across all playground matches.',
      icon: 'Trophy',
      category: 'basketball',
      tier: 'gold',
      targetValue: 100,
      currentValue: Math.floor(totalPointsScored),
      xpReward: 500,
      unlockedAt: totalPointsScored >= 100 ? '2026-08-02' : undefined,
      tips: 'Consistently log stats after your pickup runs to cross 100 PTS.',
    },
    {
      id: 'm_50_reb',
      name: '50 Career Rebounds Glass Cleaner',
      description: 'Collect 50 total career rebounds across basketball or volleyball matches.',
      icon: 'ShieldCheck',
      category: 'basketball',
      tier: 'silver',
      targetValue: 50,
      currentValue: totalRebounds,
      xpReward: 400,
      unlockedAt: totalRebounds >= 50 ? '2026-08-03' : undefined,
      tips: 'Crash the glass and log defensive & offensive boards.',
    },
    {
      id: 'm_25_ast',
      name: '25 Assists Playmaker',
      description: 'Record 25 total career assists.',
      icon: 'Zap',
      category: 'basketball',
      tier: 'silver',
      targetValue: 25,
      currentValue: totalAssists,
      xpReward: 350,
      unlockedAt: totalAssists >= 25 ? '2026-08-04' : undefined,
      tips: 'Set up open teammates during games.',
    },
    {
      id: 'm_level_10',
      name: 'Level 10 Playground Starter',
      description: 'Climb the progressive leveling ladder to Athlete Level 10.',
      icon: 'Crown',
      category: 'general',
      tier: 'gold',
      targetValue: 10,
      currentValue: user.level,
      xpReward: 600,
      unlockedAt: user.level >= 10 ? '2026-08-05' : undefined,
      tips: 'Earn XP from daily runs, achievements, and stat logging.',
    },
    {
      id: 'm_level_20',
      name: 'Level 20 League All-Star',
      description: 'Climb the progressive leveling ladder to Athlete Level 20.',
      icon: 'Star',
      category: 'general',
      tier: 'platinum',
      targetValue: 20,
      currentValue: user.level,
      xpReward: 1200,
      unlockedAt: user.level >= 20 ? '2026-08-05' : undefined,
      tips: 'Maintain a 7+ day check-in streak and log match victories.',
    },
    {
      id: 'm_level_30',
      name: 'Level 30 League MVP',
      description: 'Climb the progressive leveling ladder to Athlete Level 30.',
      icon: 'Award',
      category: 'general',
      tier: 'diamond',
      targetValue: 30,
      currentValue: user.level,
      xpReward: 2500,
      unlockedAt: user.level >= 30 ? '2026-08-05' : undefined,
      tips: 'Dominance across multiple sports unlocks high level XP bonuses.',
    },
    {
      id: 'm_level_50',
      name: 'Level 50 Immortal GOAT',
      description: 'Reach the pinnacle of performance: Athlete Level 50!',
      icon: 'Crown',
      category: 'general',
      tier: 'diamond',
      targetValue: 50,
      currentValue: user.level,
      xpReward: 5000,
      unlockedAt: user.level >= 50 ? '2026-08-05' : undefined,
      tips: 'The ultimate badge reserved for the top athletes in the Playground League.',
    },
    {
      id: 'm_triple_double',
      name: 'Triple Double Machine',
      description: 'Achieve double-digit stats in 3 categories in Basketball (PTS, REB, AST) or Volleyball (Kills, Digs, Blocks/Assists).',
      icon: 'Zap',
      category: 'general',
      tier: 'diamond',
      targetValue: 1,
      currentValue: ((user.stats?.basketball?.pts || 0) >= 10 && (user.stats?.basketball?.reb || 0) >= 10 && (user.stats?.basketball?.ast || 0) >= 10) || ((user.stats?.volleyball?.kills || 0) >= 10 && (user.stats?.volleyball?.digs || 0) >= 10 && ((user.stats?.volleyball?.blocks || 0) >= 10 || (user.stats?.volleyball?.assists || 0) >= 10)) ? 1 : 0,
      xpReward: 750,
      unlockedAt: ((user.stats?.basketball?.pts || 0) >= 10 && (user.stats?.basketball?.reb || 0) >= 10 && (user.stats?.basketball?.ast || 0) >= 10) || ((user.stats?.volleyball?.kills || 0) >= 10 && (user.stats?.volleyball?.digs || 0) >= 10 && ((user.stats?.volleyball?.blocks || 0) >= 10 || (user.stats?.volleyball?.assists || 0) >= 10)) ? '2026-08-05' : undefined,
      tips: 'Dominate the floor across scoring, glass, and playmaking!',
    },
    {
      id: 'm_century_mark',
      name: 'Century Mark',
      description: 'Reach 100+ total combined scoring points or volleyball spike kills.',
      icon: 'Flame',
      category: 'general',
      tier: 'gold',
      targetValue: 100,
      currentValue: Math.floor(totalPointsScored + (user.stats?.volleyball?.kills || 0)),
      xpReward: 500,
      unlockedAt: (totalPointsScored + (user.stats?.volleyball?.kills || 0)) >= 100 ? '2026-08-05' : undefined,
      tips: 'Log game stats consistently across playground runs.',
    },
    {
      id: 'm_vb_spike_cannon',
      name: 'Spike Cannon Specialist',
      description: 'Execute 30 total career spike kills on the volleyball court.',
      icon: 'Trophy',
      category: 'volleyball',
      tier: 'gold',
      targetValue: 30,
      currentValue: user.stats?.volleyball?.kills || 0,
      xpReward: 400,
      unlockedAt: (user.stats?.volleyball?.kills || 0) >= 30 ? '2026-08-05' : undefined,
      tips: 'Power spike down the line or over the net block!',
    },
    {
      id: 'm_vb_ace_master',
      name: 'Ace Master',
      description: 'Serve 15 unreturnable service aces in volleyball matches.',
      icon: 'Zap',
      category: 'volleyball',
      tier: 'platinum',
      targetValue: 15,
      currentValue: user.stats?.volleyball?.aces || 0,
      xpReward: 350,
      unlockedAt: (user.stats?.volleyball?.aces || 0) >= 15 ? '2026-08-05' : undefined,
      tips: 'Float serve or jump serve into deep open court corners!',
    },
    {
      id: 'm_vb_net_guardian',
      name: 'Net Wall Guardian',
      description: 'Stuff opponent spikes with 15 total career net blocks in volleyball.',
      icon: 'ShieldCheck',
      category: 'volleyball',
      tier: 'silver',
      targetValue: 15,
      currentValue: user.stats?.volleyball?.blocks || 0,
      xpReward: 300,
      unlockedAt: (user.stats?.volleyball?.blocks || 0) >= 15 ? '2026-08-05' : undefined,
      tips: 'Time your jump block at the net against opponent hitters.',
    },
    {
      id: 'm_vb_floor_defense',
      name: 'Floor Defense God',
      description: 'Log 25 total ground digs saving hard spike hits in volleyball.',
      icon: 'ShieldCheck',
      category: 'volleyball',
      tier: 'gold',
      targetValue: 25,
      currentValue: user.stats?.volleyball?.digs || 0,
      xpReward: 350,
      unlockedAt: (user.stats?.volleyball?.digs || 0) >= 25 ? '2026-08-05' : undefined,
      tips: 'Stay low in defensive position to dig up hard spikes.',
    },
    {
      id: 'm_vb_backcourt_wall',
      name: 'Backcourt Wall 🛡️',
      description: 'Volleyball Career Threshold: Reach 500 total ground digs!',
      icon: 'ShieldCheck',
      category: 'volleyball',
      tier: 'diamond',
      targetValue: 500,
      currentValue: user.stats?.volleyball?.digs || 120,
      xpReward: 1000,
      unlockedAt: (user.stats?.volleyball?.digs || 120) >= 500 ? '2026-08-06' : undefined,
      tips: 'Defensive pillar of the backcourt court lines.',
    },
    {
      id: 'm_vb_ace_striker',
      name: 'Ace Striker ⚡',
      description: 'Volleyball Career Threshold: Hit 50 service aces in matches.',
      icon: 'Zap',
      category: 'volleyball',
      tier: 'platinum',
      targetValue: 50,
      currentValue: user.stats?.volleyball?.aces || 45,
      xpReward: 750,
      unlockedAt: (user.stats?.volleyball?.aces || 45) >= 50 ? '2026-08-06' : undefined,
      tips: 'Deliver high-velocity jump serves to corner lines.',
    },
    {
      id: 'm_vb_block_party',
      name: 'Block Party 🧱',
      description: 'Volleyball Milestone: Stuff opponents with 20 total net blocks.',
      icon: 'Crown',
      category: 'volleyball',
      tier: 'gold',
      targetValue: 20,
      currentValue: user.stats?.volleyball?.blocks || 35,
      xpReward: 500,
      unlockedAt: (user.stats?.volleyball?.blocks || 35) >= 20 ? '2026-08-06' : undefined,
      tips: 'Shut down front net spike angles.',
    },
    {
      id: 'm_vb_setting_maestro',
      name: 'Setting Maestro',
      description: 'Dish out 30 setting assists in 6v6 or Beach Volleyball.',
      icon: 'Star',
      category: 'volleyball',
      tier: 'gold',
      targetValue: 30,
      currentValue: user.stats?.volleyball?.assists || 0,
      xpReward: 400,
      unlockedAt: (user.stats?.volleyball?.assists || 0) >= 30 ? '2026-08-05' : undefined,
      tips: 'Set high soft balls for your outside hitters and middle blockers.',
    },
    // ELITE SERIES (Exclusively for Verified PRO Athletes)
    {
      id: 'm_pro_vanguard_scorer',
      name: 'Pro Vanguard Scorer 👑',
      description: 'PRO Verified Milestone: Reach 120+ verified career points in official league games.',
      icon: 'Sparkles',
      category: 'elite_series',
      tier: 'pro_elite',
      targetValue: 120,
      currentValue: Math.floor(totalPointsScored),
      xpReward: 1000,
      unlockedAt: (user.isPro || user.isVerifiedPro) && totalPointsScored >= 120 ? '2026-08-05' : undefined,
      tips: 'PRO subscribers earn verified points audit badges after scout verification.',
      isProVerified: true,
    },
    {
      id: 'm_pro_playmaker',
      name: 'Scout Certified Playmaker 👁️',
      description: 'PRO Verified Milestone: Reach 30+ verified assists logged with video highlights.',
      icon: 'Eye',
      category: 'elite_series',
      tier: 'pro_elite',
      targetValue: 30,
      currentValue: totalAssists,
      xpReward: 850,
      unlockedAt: (user.isPro || user.isVerifiedPro) && totalAssists >= 30 ? '2026-08-05' : undefined,
      tips: 'Upload 30s assist clips to verify playmaker stats with scout moderators.',
      isProVerified: true,
    },
    {
      id: 'm_pro_legend_wins',
      name: 'Verified Court Legend 💎',
      description: 'PRO Verified Milestone: Secure 8+ verified victories in PRO league matchups.',
      icon: 'Award',
      category: 'elite_series',
      tier: 'pro_elite',
      targetValue: 8,
      currentValue: user.winCount || 0,
      xpReward: 1200,
      unlockedAt: (user.isPro || user.isVerifiedPro) && (user.winCount || 0) >= 8 ? '2026-08-05' : undefined,
      tips: 'Compete in PRO verified matches to build your elite record.',
      isProVerified: true,
    },
    {
      id: 'm_pro_euro_prospect',
      name: 'EuroLeague Prospect 🌍',
      description: 'PRO Verified Milestone: Reach 150+ PTS with certified moderator audit.',
      icon: 'Flame',
      category: 'elite_series',
      tier: 'pro_elite',
      targetValue: 150,
      currentValue: Math.floor(totalPointsScored),
      xpReward: 1500,
      unlockedAt: (user.isPro || user.isVerifiedPro) && totalPointsScored >= 150 ? '2026-08-05' : undefined,
      tips: 'Highest tier milestone reserved for elite verified PRO prospects.',
      isProVerified: true,
    },
  ];

  // Helper to render icon by string name
  const renderIcon = (iconName: string, className = 'w-5 h-5') => {
    switch (iconName) {
      case 'Trophy': return <Trophy className={className} />;
      case 'Flame': return <Flame className={className} />;
      case 'Award': return <Award className={className} />;
      case 'ShieldCheck': return <ShieldCheck className={className} />;
      case 'Crown': return <Crown className={className} />;
      case 'Target': return <Target className={className} />;
      case 'Users': return <Users className={className} />;
      case 'Film': return <Film className={className} />;
      case 'Zap': return <Zap className={className} />;
      case 'Star': return <Star className={className} />;
      case 'MapPin': return <MapPin className={className} />;
      case 'Medal': return <Medal className={className} />;
      case 'Dumbbell': return <Dumbbell className={className} />;
      case 'Activity': return <Activity className={className} />;
      default: return <Sparkles className={className} />;
    }
  };

  // Tier Colors & Styling
  const getTierBadge = (tier: MilestoneItem['tier']) => {
    switch (tier) {
      case 'bronze':
        return {
          label: 'Bronze',
          bg: 'bg-amber-900/40 text-amber-300 border-amber-600/40',
          glow: 'from-amber-600/20 to-transparent',
          text: 'text-amber-400',
        };
      case 'silver':
        return {
          label: 'Silver',
          bg: 'bg-slate-800/80 text-slate-200 border-slate-400/40',
          glow: 'from-slate-400/20 to-transparent',
          text: 'text-slate-300',
        };
      case 'gold':
        return {
          label: 'Gold',
          bg: 'bg-yellow-900/40 text-yellow-300 border-yellow-500/40',
          glow: 'from-yellow-500/20 to-transparent',
          text: 'text-yellow-400',
        };
      case 'platinum':
        return {
          label: 'Platinum',
          bg: 'bg-cyan-900/40 text-cyan-300 border-cyan-400/40',
          glow: 'from-cyan-400/20 to-transparent',
          text: 'text-cyan-300',
        };
      case 'diamond':
        return {
          label: 'Diamond',
          bg: 'bg-purple-900/40 text-purple-300 border-purple-400/40',
          glow: 'from-purple-500/20 to-transparent',
          text: 'text-purple-300',
        };
      case 'pro_elite':
        return {
          label: 'PRO Elite 👑',
          bg: 'bg-gradient-to-r from-amber-400/30 to-yellow-300/30 text-amber-300 border-amber-400/80 shadow-[0_0_15px_rgba(251,191,36,0.4)]',
          glow: 'from-amber-400/40 via-yellow-300/20 to-amber-500/10',
          text: 'text-amber-300 font-black',
        };
      case 'legendary':
        return {
          label: 'Legendary 👑',
          bg: 'bg-gradient-to-r from-amber-400/50 via-yellow-200/50 to-amber-500/50 text-amber-200 border-amber-300 shadow-[0_0_20px_rgba(251,191,36,0.6)]',
          glow: 'from-amber-300/50 via-yellow-200/30 to-amber-500/20',
          text: 'text-amber-200 font-black tracking-wider uppercase',
        };
    }
  };

  // Filter Logic
  const filteredMilestones = milestonesList.filter((m) => {
    const isUnlocked = m.currentValue >= m.targetValue;
    const matchesSearch = 
      m.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.description.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesCategory = filterCategory === 'all' || m.category === filterCategory;
    const matchesStatus = 
      filterStatus === 'all' ||
      (filterStatus === 'unlocked' && isUnlocked) ||
      (filterStatus === 'locked' && !isUnlocked);

    return matchesSearch && matchesCategory && matchesStatus;
  });

  // Calculate totals
  const unlockedCount = milestonesList.filter((m) => m.currentValue >= m.targetValue).length;
  const totalCount = milestonesList.length;
  const totalXpEarned = milestonesList
    .filter((m) => m.currentValue >= m.targetValue)
    .reduce((acc, curr) => acc + curr.xpReward, 0);

  const [announcementModal, setAnnouncementModal] = useState<{
    isOpen: boolean;
    level: number;
    challengeTitle: string;
    description: string;
    rewardXp: number;
  } | null>(null);

  const handleTogglePin = (badgeId: string) => {
    const isPinned = pinnedBadgeIds.includes(badgeId);
    const updated = isPinned
      ? pinnedBadgeIds.filter((id) => id !== badgeId)
      : [...pinnedBadgeIds, badgeId];

    setPinnedBadgeIds(updated);

    if (onUpdateProfile) {
      onUpdateProfile({ pinnedBadgeIds: updated });
    }
    saveAthleteToFirestore({ ...user, pinnedBadgeIds: updated });

    setShowcaseToastMsg(
      isPinned
        ? '📌 Badge unpinned from your Profile Showcase Reel.'
        : '📌 Badge pinned to your Profile Showcase Reel!'
    );
    setTimeout(() => setShowcaseToastMsg(null), 3500);
  };

  const handleClaim = (m: MilestoneItem) => {
    setClaimedBadges((prev) => ({ ...prev, [m.id]: true }));
    
    // AchievementXPIntegration: flows claimed points into global XP & 50-level progression system
    const result = claimAchievementXP(user, m.xpReward, m.name);

    if (onUpdateProfile) {
      onUpdateProfile(result.updatedUser);
    }
    if (onClaimXp) {
      onClaimXp(m.xpReward, m.name);
    }

    const isLegendaryRank = m.tier === 'legendary' || m.tier === 'diamond' || m.tier === 'pro_elite' || m.id === 'm_level_50' || m.id === 'm_hall_of_famer';

    // Trigger unique, high-tier animation when unlocking Legendary rank or high milestone
    setConfettiConfig({
      isVisible: true,
      tier: isLegendaryRank ? 'legendary' : 'standard',
      title: isLegendaryRank ? '👑 LEGENDARY RANK UNLOCKED!' : '🏆 ACHIEVEMENT UNLOCKED!',
      subtitle: `${m.name} (+${m.xpReward} XP)`,
    });
    setTimeout(() => {
      setConfettiConfig((prev) => ({ ...prev, isVisible: false }));
    }, 4500);

    // Save dedicated 'Achievement unlocked' toast notification to Firestore for NotificationDrawer
    const notif: AppNotification = {
      id: `notif_achieve_${Date.now()}`,
      type: 'achievement',
      title: `🏆 Achievement Unlocked: ${m.name}`,
      message: `Congratulations! You unlocked the ${m.tier.toUpperCase()} ${m.name} badge and earned +${m.xpReward} XP!`,
      timestamp: 'Just now',
      createdAt: Date.now(),
      read: false,
    };
    saveNotificationToFirestore(notif);

    // Trigger 'Elite Challenge' UI announcement when athlete hits milestone levels 10, 25, or 50 or levels up
    if (result.isMilestoneReached || result.leveledUp) {
      const announce = result.levelInfo.eliteChallengeAnnouncement;
      if (announce) {
        setAnnouncementModal({
          isOpen: true,
          level: result.newLevel,
          challengeTitle: announce.title,
          description: announce.description,
          rewardXp: announce.xpReward,
        });
      }
    }
  };

  const handleShowAnnouncementForLevel = (targetLvl: number) => {
    const challenge = ELITE_CHALLENGES.find((c) => c.unlockedAtLevel === targetLvl) || {
      id: `challenge_lvl${targetLvl}`,
      unlockedAtLevel: targetLvl,
      title: `Level ${targetLvl} Elite Milestone`,
      description: `Specific high-tier milestone reached at Level ${targetLvl}. Maintain top performance across official matches.`,
      xpReward: targetLvl * 100,
    };
    setAnnouncementModal({
      isOpen: true,
      level: targetLvl,
      challengeTitle: challenge.title,
      description: challenge.description,
      rewardXp: challenge.xpReward,
    });
    fireEliteSeriesConfetti();
  };

  // Helper for rounded rectangle canvas drawing
  const drawRoundRect = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    r: number,
    fill: boolean,
    stroke: boolean
  ) => {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
    if (fill) ctx.fill();
    if (stroke) ctx.stroke();
  };

  // Canvas Snapshot Image Generator using active theme palette
  const generateSnapshotDataUrl = (m: MilestoneItem): string => {
    const canvas = document.createElement('canvas');
    canvas.width = 1200;
    canvas.height = 675;
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';

    // Color palette resolution from theme
    const currentTheme = activePalette || 'midnight_chrome';
    let bgGradStart = '#090d16';
    let bgGradEnd = '#121829';
    let accentHex = '#a3e635'; // Lime
    let secondaryHex = '#818cf8';

    if (currentTheme === 'neon_court') {
      bgGradStart = '#030712';
      bgGradEnd = '#0b1329';
      accentHex = '#22d3ee'; // Cyan
      secondaryHex = '#e879f9';
    } else if (currentTheme === 'sunset_horizon') {
      bgGradStart = '#140b12';
      bgGradEnd = '#241221';
      accentHex = '#fbbf24'; // Amber
      secondaryHex = '#fb7185';
    } else if (currentTheme === 'cyber_arena') {
      bgGradStart = '#041410';
      bgGradEnd = '#0a261f';
      accentHex = '#34d399'; // Emerald
      secondaryHex = '#38bdf8';
    }

    // Main Gradient Background
    const bgGrad = ctx.createLinearGradient(0, 0, 1200, 675);
    bgGrad.addColorStop(0, bgGradStart);
    bgGrad.addColorStop(1, bgGradEnd);
    ctx.fillStyle = bgGrad;
    ctx.fillRect(0, 0, 1200, 675);

    // Glowing Radial Accent Spotlight
    const glowGrad = ctx.createRadialGradient(600, 337, 30, 600, 337, 500);
    glowGrad.addColorStop(0, accentHex + '35');
    glowGrad.addColorStop(1, 'transparent');
    ctx.fillStyle = glowGrad;
    ctx.fillRect(0, 0, 1200, 675);

    // Card Container Frame
    ctx.fillStyle = bgGradEnd;
    ctx.strokeStyle = accentHex + '88';
    ctx.lineWidth = 4;
    drawRoundRect(ctx, 60, 50, 1080, 575, 40, true, true);

    // Top Header Badge Tag
    ctx.fillStyle = accentHex;
    ctx.font = 'bold italic 22px sans-serif';
    ctx.fillText('PLAYGROUNDS SPORTS PLATFORM • ATHLETE SNAPSHOT', 100, 110);

    // Athlete Name
    ctx.fillStyle = '#ffffff';
    ctx.font = '900 italic 42px sans-serif';
    ctx.fillText((user?.name || 'Athlete').toUpperCase(), 100, 170);

    // Athlete Handle & Level Title
    ctx.fillStyle = '#94a3b8';
    ctx.font = 'bold 24px sans-serif';
    ctx.fillText(`@${user?.handle || 'athlete'}  •  LEVEL ${user?.level || 1} ${user?.title || 'ATHLETE'}`, 100, 210);

    // Divider Line
    ctx.strokeStyle = '#ffffff22';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(100, 240);
    ctx.lineTo(1100, 240);
    ctx.stroke();

    // Badge Emblem Box
    ctx.fillStyle = accentHex + '22';
    ctx.strokeStyle = accentHex;
    ctx.lineWidth = 3;
    drawRoundRect(ctx, 100, 280, 160, 160, 30, true, true);

    // Trophy Emblem Symbol
    ctx.fillStyle = accentHex;
    ctx.font = 'bold 80px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('🏆', 180, 390);
    ctx.textAlign = 'left';

    // Badge Title & Tier Tag
    ctx.fillStyle = '#ffffff';
    ctx.font = '900 italic 46px sans-serif';
    ctx.fillText(m.name.toUpperCase(), 290, 335);

    ctx.fillStyle = accentHex;
    ctx.font = 'bold 24px sans-serif';
    ctx.fillText(`${m.tier.toUpperCase()} TIER BADGE  •  +${m.xpReward} XP REWARD`, 290, 375);

    // Badge Description
    ctx.fillStyle = '#cbd5e1';
    ctx.font = '500 22px sans-serif';
    ctx.fillText(m.description, 290, 420);

    // Footer Watermark & Unlock Date
    ctx.fillStyle = '#94a3b8';
    ctx.font = 'bold 20px sans-serif';
    ctx.fillText(`UNLOCKED: ${m.unlockedAt || 'Recently Earned'}`, 100, 560);

    ctx.fillStyle = accentHex;
    ctx.font = 'bold italic 22px sans-serif';
    ctx.fillText('VERIFIED ATHLETE BADGE ✓', 800, 560);

    return canvas.toDataURL('image/png');
  };

  const handleOpenShareModal = (milestone?: MilestoneItem) => {
    // If specific milestone passed, use it, otherwise find the latest/highest unlocked milestone
    const target = milestone || milestonesList.find((m) => m.currentValue >= m.targetValue) || milestonesList[0];
    setShareMilestone(target);
    setIsShareModalOpen(true);
  };

  // Sport-specific default highlight clips
  const sportDefaultClips: Record<string, HighlightClip[]> = {
    volleyball: [
      {
        id: 'clip_vb_1',
        title: 'Monster Solo Block at Net 🏐',
        sport: 'volleyball',
        videoUrl: 'https://assets.mixkit.co/videos/preview/mixkit-beach-volleyball-game-in-the-sun-41855-large.mp4',
        thumbnailUrl: 'https://images.unsplash.com/photo-1612872087720-bb876e2e67d1?auto=format&fit=crop&q=80&w=600',
        durationSeconds: 15,
        createdAt: '2 hours ago',
        views: 380,
        likes: 95,
        description: 'Volleyball Drill & Match Highlight: Stuffed the opponent spike at the net!',
      },
      {
        id: 'clip_vb_2',
        title: 'Unstoppable Jump Serve Ace in Clutch 🏐',
        sport: 'volleyball',
        videoUrl: 'https://assets.mixkit.co/videos/preview/mixkit-beach-volleyball-game-in-the-sun-41855-large.mp4',
        thumbnailUrl: 'https://images.unsplash.com/photo-1592656094267-764a45160876?auto=format&fit=crop&q=80&w=600',
        durationSeconds: 22,
        createdAt: '1 day ago',
        views: 512,
        likes: 124,
        description: 'Perfect topspin jump serve targeting the deep back line.',
      },
      {
        id: 'clip_vb_3',
        title: 'Backrow Pipe Attack Spike Finish 🏐',
        sport: 'volleyball',
        videoUrl: 'https://assets.mixkit.co/videos/preview/mixkit-beach-volleyball-game-in-the-sun-41855-large.mp4',
        thumbnailUrl: 'https://images.unsplash.com/photo-1587280501635-68a0e82cd5ff?auto=format&fit=crop&q=80&w=600',
        durationSeconds: 19,
        createdAt: '3 days ago',
        views: 289,
        likes: 72,
        description: 'Dynamic setter quick-set to backrow pipe attack for clean kill.',
      },
    ],
    basketball: [
      {
        id: 'clip_bk_1',
        title: 'Clutch Game-Winner Stepback 3-Pointer 🏀',
        sport: 'basketball',
        videoUrl: 'https://assets.mixkit.co/videos/preview/mixkit-basketball-player-dunking-a-ball-40866-large.mp4',
        thumbnailUrl: 'https://images.unsplash.com/photo-1546519638-68e109498ffc?auto=format&fit=crop&q=80&w=600',
        durationSeconds: 18,
        createdAt: '1 hour ago',
        views: 245,
        likes: 64,
        description: 'Level 1 Challenge completed: 3-Pointer in 4th quarter at Rucker Park!',
      },
    ],
  };

  // Filter user highlights or default clips to match user's primary sport
  const userClips = (user.highlights && user.highlights.length > 0)
    ? user.highlights.filter((h) => !h.sport || h.sport === userSport)
    : [];

  const showcaseClips = userClips.length > 0
    ? userClips
    : (sportDefaultClips[userSport] || sportDefaultClips.volleyball);

  // Filter pinned milestones: pinned + sport-matched + rarity filter
  const pinnedMilestones = milestonesList.filter((m) => {
    const isPinned = pinnedBadgeIds.includes(m.id);
    if (!isPinned) return false;

    // Filter by user's sport: if badge has a sport category that doesn't match athlete's primary sport, hide it
    if (m.category && m.category !== 'general' && m.category !== userSport) {
      return false;
    }

    if (showcaseRarityFilter === 'all') return true;
    if (showcaseRarityFilter === 'diamond') {
      return m.tier === 'diamond' || m.tier === 'pro_elite' || m.tier === 'legendary';
    }
    return m.tier === showcaseRarityFilter;
  });

  return (
    <div className="space-y-6 text-white animate-fadeIn">
      {/* High-Tier Legendary Confetti Explosion */}
      <ConfettiExplosion
        isVisible={confettiConfig.isVisible}
        tier={confettiConfig.tier}
        title={confettiConfig.title}
        subtitle={confettiConfig.subtitle}
      />

      {/* Combined Hub Navigation Tabs Bar */}
      <div className="flex items-center space-x-2 bg-indigo-950/90 p-2 rounded-3xl border border-white/10 shadow-xl overflow-x-auto">
        <button
          type="button"
          onClick={() => setActiveHubTab('showcase')}
          className={`flex items-center space-x-2 px-5 py-3 rounded-2xl font-black italic uppercase text-xs transition shrink-0 ${
            activeHubTab === 'showcase'
              ? 'bg-gradient-to-r from-amber-400 via-lime-400 to-emerald-400 text-black shadow-lg shadow-lime-400/20'
              : 'text-indigo-200 hover:text-white hover:bg-indigo-900/50'
          }`}
        >
          <Crown className="w-4 h-4 fill-amber-900 text-amber-900" />
          <span>Profile Showcase</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveHubTab('achievements')}
          className={`flex items-center space-x-2 px-5 py-3 rounded-2xl font-black italic uppercase text-xs transition shrink-0 ${
            activeHubTab === 'achievements'
              ? 'bg-lime-400 text-black shadow-lg shadow-lime-400/20'
              : 'text-indigo-200 hover:text-white hover:bg-indigo-900/50'
          }`}
        >
          <Trophy className="w-4 h-4" />
          <span>Badges & Achievements ({unlockedCount}/{totalCount})</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveHubTab('reels')}
          className={`flex items-center space-x-2 px-5 py-3 rounded-2xl font-black italic uppercase text-xs transition shrink-0 ${
            activeHubTab === 'reels'
              ? 'bg-lime-400 text-black shadow-lg shadow-lime-400/20'
              : 'text-indigo-200 hover:text-white hover:bg-indigo-900/50'
          }`}
        >
          <Film className="w-4 h-4 text-purple-400" />
          <span>Reels & Community</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveHubTab('challenges')}
          className={`flex items-center space-x-2 px-5 py-3 rounded-2xl font-black italic uppercase text-xs transition shrink-0 ${
            activeHubTab === 'challenges'
              ? 'bg-lime-400 text-black shadow-lg shadow-lime-400/20'
              : 'text-indigo-200 hover:text-white hover:bg-indigo-900/50'
          }`}
        >
          <Target className="w-4 h-4 text-amber-400" />
          <span>Sport Drills & Training</span>
        </button>
      </div>

      {showcaseToastMsg && (
        <div className="p-3 bg-gradient-to-r from-amber-500/20 to-lime-400/20 border border-amber-400/50 rounded-2xl text-center text-xs font-black italic uppercase text-amber-300 animate-fadeIn flex items-center justify-center space-x-2">
          <Sparkles className="w-4 h-4 text-amber-400 animate-pulse" />
          <span>{showcaseToastMsg}</span>
        </div>
      )}

      {/* PROFILE SHOWCASE UNIFIED VIEW */}
      {activeHubTab === 'showcase' && (
        <div className="space-y-6">
          {/* Rank Level Cumulative Progress Bar Section */}
          {(() => {
            const levelInfo = getLevelInfo(user.totalXp || user.xp || 0);
            return (
              <div className="bg-gradient-to-r from-indigo-950 via-purple-900 to-slate-950 p-5 rounded-[2rem] border border-lime-400/40 shadow-2xl space-y-3 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-lime-400/10 rounded-full blur-2xl pointer-events-none" />
                
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 relative z-10">
                  <div className="flex items-center space-x-3">
                    <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-amber-400 via-yellow-300 to-lime-400 text-black flex items-center justify-center font-black text-sm shadow-xl shrink-0">
                      L{levelInfo.level}
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="text-xs font-black italic uppercase text-lime-400 tracking-wider">
                          Rank Progression
                        </span>
                        <span className="text-[10px] bg-indigo-900 text-amber-300 px-2.5 py-0.5 rounded-full font-black border border-amber-400/30">
                          {levelInfo.levelTitle}
                        </span>
                      </div>
                      <p className="text-sm font-black text-white mt-0.5">
                        Level {levelInfo.level} Athlete • <span className="text-lime-300">{(user.totalXp || user.xp || 0).toLocaleString()} XP</span> Total Career Progress
                      </p>
                    </div>
                  </div>

                  <div className="text-left sm:text-right text-xs font-mono font-bold text-indigo-200">
                    {levelInfo.isMaxLevel ? (
                      <span className="text-amber-300 font-black flex items-center space-x-1">
                        <Crown className="w-4 h-4 text-amber-400 fill-amber-400" />
                        <span>MAX LEVEL REACHED</span>
                      </span>
                    ) : (
                      <div className="space-y-0.5">
                        <p className="text-[11px] text-indigo-300 uppercase font-black">
                          Next Rank Goal: Level {levelInfo.level + 1}
                        </p>
                        <p className="text-xs font-black text-lime-400">
                          {levelInfo.xpInCurrentLevel.toLocaleString()} / {levelInfo.xpRequiredForNextLevel.toLocaleString()} XP ({levelInfo.progressPct}%)
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Animated Level Progress Bar */}
                <div className="w-full h-4 bg-indigo-950/90 rounded-full overflow-hidden p-0.5 border border-white/15 shadow-inner relative z-10">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${levelInfo.progressPct}%` }}
                    transition={{ duration: 1.2, ease: 'easeOut' }}
                    className="h-full bg-gradient-to-r from-lime-400 via-amber-400 to-emerald-400 rounded-full shadow-[0_0_15px_rgba(163,230,53,0.8)] relative"
                  >
                    <div className="absolute inset-0 bg-white/20 rounded-full animate-pulse" />
                  </motion.div>
                </div>
              </div>
            );
          })()}

          {/* Athlete Profile Banner */}
          <div className="bg-gradient-to-br from-indigo-950 via-purple-950 to-slate-950 p-6 sm:p-8 rounded-[2.5rem] border border-white/20 shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-96 h-96 bg-lime-400/10 rounded-full blur-3xl pointer-events-none" />
            
            <div className="flex flex-col md:flex-row items-center justify-between gap-6 relative z-10">
              <div className="flex flex-col sm:flex-row items-center text-center sm:text-left space-y-4 sm:space-y-0 sm:space-x-6">
                <div className="relative shrink-0">
                  <img
                    src={user.avatar}
                    alt={user.name}
                    className="w-24 h-24 sm:w-28 sm:h-28 rounded-3xl object-cover ring-4 ring-lime-400/80 shadow-2xl"
                  />
                  <div className="absolute -bottom-2 -right-2 bg-gradient-to-r from-amber-400 to-yellow-300 text-black px-3 py-1 rounded-xl text-xs font-black uppercase tracking-wider shadow-lg flex items-center space-x-1">
                    <Crown className="w-3.5 h-3.5 fill-black" />
                    <span>LVL {user.level}</span>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <div className="flex items-center justify-center sm:justify-start space-x-2">
                    <h1 className="text-2xl sm:text-3xl font-black italic uppercase text-white tracking-tight">
                      {user.name}
                    </h1>
                    {(user.isPro || user.isVerifiedPro) && (
                      <span className="bg-gradient-to-r from-amber-400 to-yellow-300 text-black text-[10px] font-black uppercase px-2.5 py-0.5 rounded-full flex items-center shadow-md">
                        <Crown className="w-3 h-3 mr-0.5 fill-black" /> PRO
                      </span>
                    )}
                  </div>
                  
                  <p className="text-xs text-indigo-300 font-bold">
                    @{user.handle} • {user.title || 'Playground League Athlete'}
                  </p>

                  <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 pt-1">
                    <span className="bg-indigo-900/80 text-lime-400 border border-lime-400/30 px-2.5 py-1 rounded-xl text-[11px] font-mono font-black flex items-center space-x-1">
                      <Sparkles className="w-3.5 h-3.5 text-lime-400" />
                      <span>{user.totalXp} XP</span>
                    </span>
                    <span className="bg-indigo-900/80 text-amber-300 border border-amber-400/30 px-2.5 py-1 rounded-xl text-[11px] font-mono font-black flex items-center space-x-1">
                      <Flame className="w-3.5 h-3.5 text-amber-400" />
                      <span>{user.dailyStreak || 1} Day Streak</span>
                    </span>
                    <span className="bg-indigo-900/80 text-cyan-300 border border-cyan-400/30 px-2.5 py-1 rounded-xl text-[11px] font-mono font-black flex items-center space-x-1">
                      <Trophy className="w-3.5 h-3.5 text-cyan-400" />
                      <span>{user.winCount || 0} Wins</span>
                    </span>
                    <span className="bg-indigo-900/80 text-purple-300 border border-purple-400/30 px-2.5 py-1 rounded-xl text-[11px] font-mono font-black uppercase flex items-center space-x-1">
                      <span>🏐 {userSport}</span>
                    </span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center space-x-3 w-full md:w-auto">
                <button
                  type="button"
                  onClick={() => setActiveHubTab('achievements')}
                  className="flex-1 md:flex-none px-4 py-3 bg-indigo-900/90 hover:bg-indigo-800 text-white font-black italic text-xs uppercase rounded-2xl border border-white/15 shadow-lg transition flex items-center justify-center space-x-2"
                >
                  <Pin className="w-4 h-4 text-amber-400" />
                  <span>Pin Badges</span>
                </button>
                <button
                  type="button"
                  onClick={() => onOpenUploadModal?.()}
                  className="flex-1 md:flex-none px-5 py-3 bg-lime-400 hover:bg-lime-300 text-black font-black italic text-xs uppercase rounded-2xl shadow-xl shadow-lime-400/20 transition flex items-center justify-center space-x-2"
                >
                  <Video className="w-4 h-4" />
                  <span>Upload Highlight</span>
                </button>
              </div>
            </div>
          </div>

          {/* Unified Grid: Pinned Career Badges + Highlight Reels */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* LEFT COLUMN: Featured Pinned Accomplishments */}
            <div className="bg-indigo-950/80 p-6 rounded-[2.5rem] border border-white/10 shadow-xl space-y-5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-white/10 pb-4 gap-3">
                <div className="flex items-center space-x-2.5">
                  <div className="p-2.5 bg-gradient-to-r from-amber-400 to-yellow-300 text-black rounded-2xl shadow-md">
                    <Pin className="w-5 h-5 fill-black" />
                  </div>
                  <div>
                    <h2 className="text-lg font-black italic uppercase text-white tracking-tight">
                      Featured Badges
                    </h2>
                    <p className="text-[11px] text-indigo-300 font-semibold">
                      Pinned accomplishments for {userSport}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  {/* Rarity Filter Dropdown */}
                  <div className="flex items-center space-x-1.5 bg-indigo-900/90 px-3 py-1.5 rounded-xl border border-white/15 text-xs font-black">
                    <Filter className="w-3.5 h-3.5 text-amber-400" />
                    <select
                      value={showcaseRarityFilter}
                      onChange={(e) => setShowcaseRarityFilter(e.target.value as any)}
                      className="bg-transparent text-white text-[11px] font-black italic uppercase focus:outline-none cursor-pointer"
                    >
                      <option value="all" className="bg-indigo-950 text-white">All Rarities</option>
                      <option value="bronze" className="bg-indigo-950 text-amber-600">🥉 Bronze</option>
                      <option value="silver" className="bg-indigo-950 text-slate-300">🥈 Silver</option>
                      <option value="gold" className="bg-indigo-950 text-yellow-400">🥇 Gold</option>
                      <option value="platinum" className="bg-indigo-950 text-cyan-300">💎 Platinum</option>
                      <option value="diamond" className="bg-indigo-950 text-purple-300">👑 Legendary</option>
                    </select>
                  </div>

                  <button
                    type="button"
                    onClick={() => setActiveHubTab('achievements')}
                    className="px-3 py-1.5 bg-indigo-900 hover:bg-indigo-800 text-lime-400 text-xs font-black italic uppercase rounded-xl border border-lime-400/30 transition flex items-center space-x-1 shrink-0"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Pin</span>
                  </button>
                </div>
              </div>

              {/* Pinned Badges List with Scaling Hover Animation, Multi-Select & Credential Status Indicator */}
              <div className="space-y-3">
                {pinnedMilestones.length > 0 ? (
                  pinnedMilestones.map((m) => {
                    const tierMeta = getTierBadge(m.tier);
                    const isSelected = selectedBadgeIds.includes(m.id);
                    return (
                      <div
                        key={m.id}
                        onClick={() => setPreviewBadge(m)}
                        className={`p-4 bg-gradient-to-r from-indigo-900/90 to-purple-950/80 rounded-2xl border transition-all duration-300 transform hover:scale-[1.02] hover:-translate-y-1 hover:shadow-[0_0_25px_rgba(251,191,36,0.3)] cursor-pointer relative group ${
                          isSelected ? 'border-lime-400 ring-2 ring-lime-400/50 shadow-xl' : 'border-amber-400/40'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center space-x-3">
                            {/* Emblem Box with Credential Status Indicator overlay */}
                            <div className="relative shrink-0">
                              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-400 via-yellow-300 to-lime-400 text-black flex items-center justify-center shadow-lg group-hover:rotate-3 transition duration-300">
                                {renderIcon(m.icon, 'w-6 h-6 stroke-[2.5]')}
                              </div>
                              {/* Credential Status Indicator icon overlay */}
                              <div className="absolute -top-1.5 -right-1.5 bg-indigo-950 p-1 rounded-full border border-emerald-400/80 shadow-md flex items-center justify-center" title="Verified Athlete Status: Active">
                                <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
                              </div>
                            </div>

                            <div>
                              <div className="flex items-center space-x-2">
                                <span className={`text-[9px] font-black italic uppercase px-2 py-0.5 rounded-full border ${tierMeta.bg}`}>
                                  {tierMeta.label}
                                </span>
                                {(m.isProVerified || user.isPro) && (
                                  <span className="bg-gradient-to-r from-amber-400 to-yellow-300 text-black text-[9px] font-black uppercase px-2 py-0.5 rounded-full flex items-center shadow-sm">
                                    <Crown className="w-2.5 h-2.5 mr-0.5 fill-black" /> PRO
                                  </span>
                                )}
                                <span className="text-[10px] font-mono font-black text-lime-400">
                                  +{m.xpReward} XP
                                </span>
                              </div>
                              <h3 className="text-sm font-black italic uppercase text-white mt-1 group-hover:text-amber-300 transition flex items-center space-x-1.5">
                                <span>{m.name}</span>
                              </h3>
                              <p className="text-xs text-indigo-200/80 leading-relaxed line-clamp-2">
                                {m.description}
                              </p>
                            </div>
                          </div>

                          <div className="flex flex-col items-end space-y-1.5 shrink-0">
                            {/* Select Toggle Button for Multi-Select Batch Actions */}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleSelectBadge(m.id);
                              }}
                              className={`px-2.5 py-1 rounded-xl text-[10px] font-black uppercase transition flex items-center space-x-1 border ${
                                isSelected
                                  ? 'bg-lime-400 text-black border-lime-300 shadow-md shadow-lime-400/30'
                                  : 'bg-indigo-950/80 text-indigo-300 border-white/20 hover:border-lime-400/50 hover:text-white'
                              }`}
                              title="Select for batch actions"
                            >
                              {isSelected ? (
                                <>
                                  <CheckCircle2 className="w-3 h-3 fill-black text-lime-400" />
                                  <span>Selected</span>
                                </>
                              ) : (
                                <>
                                  <Square className="w-3 h-3" />
                                  <span>Select</span>
                                </>
                              )}
                            </button>

                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTogglePin(m.id);
                              }}
                              className="px-2.5 py-1 bg-amber-400/20 hover:bg-amber-400 text-amber-300 hover:text-black border border-amber-400/50 rounded-xl text-[10px] font-black uppercase transition flex items-center space-x-1"
                              title="Unpin from Showcase"
                            >
                              <Pin className="w-3 h-3 fill-current" />
                              <span>Pinned</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  /* Illustrative Empty State Component */
                  <div className="p-8 text-center bg-gradient-to-br from-indigo-950/90 via-purple-950/40 to-slate-950/90 rounded-[2rem] border-2 border-dashed border-amber-400/30 space-y-4 shadow-2xl relative overflow-hidden">
                    <div className="w-16 h-16 mx-auto rounded-3xl bg-amber-400/20 text-amber-400 flex items-center justify-center border border-amber-400/40 shadow-xl relative">
                      <Award className="w-8 h-8 stroke-[2.2]" />
                      <div className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-lime-400 animate-ping" />
                    </div>

                    <div className="max-w-xs mx-auto space-y-1.5">
                      <h3 className="text-base font-black italic uppercase text-white tracking-tight">
                        No Badges Displayed
                      </h3>
                      <p className="text-xs text-indigo-200/80 leading-relaxed font-semibold">
                        Pin your top career milestones and highlight clips to impress scouts and local athletes!
                      </p>
                    </div>

                    <div className="flex flex-col sm:flex-row items-center justify-center gap-2 pt-1">
                      <button
                        type="button"
                        onClick={() => onOpenUploadModal?.()}
                        className="w-full sm:w-auto px-4 py-2 bg-lime-400 hover:bg-lime-300 text-black text-xs font-black italic uppercase rounded-xl shadow-lg transition flex items-center justify-center space-x-1.5"
                      >
                        <Video className="w-3.5 h-3.5" />
                        <span>Upload First Highlight</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => setActiveHubTab('achievements')}
                        className="w-full sm:w-auto px-4 py-2 bg-indigo-900 hover:bg-indigo-800 text-amber-300 border border-amber-400/40 text-xs font-black italic uppercase rounded-xl shadow-lg transition flex items-center justify-center space-x-1.5"
                      >
                        <Trophy className="w-3.5 h-3.5" />
                        <span>Join Pickup Games to Earn Badges</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT COLUMN: Best Game-Play Highlight Clips */}
            <div className="bg-indigo-950/80 p-6 rounded-[2.5rem] border border-white/10 shadow-xl space-y-5">
              <div className="flex items-center justify-between border-b border-white/10 pb-4">
                <div className="flex items-center space-x-2.5">
                  <div className="p-2.5 bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-2xl shadow-md">
                    <Film className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-lg font-black italic uppercase text-white tracking-tight">
                      Best Game Highlights
                    </h2>
                    <p className="text-[11px] text-indigo-300 font-semibold">
                      Featured video clips & gameplay moments
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setActiveHubTab('reels')}
                  className="px-3 py-1.5 bg-indigo-900 hover:bg-indigo-800 text-lime-400 text-xs font-black italic uppercase rounded-xl border border-lime-400/30 transition flex items-center space-x-1"
                >
                  <span>Reel Theater</span>
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Highlight Cards Grid with Status Indicators & Hover Scaling */}
              <div className="space-y-3">
                {showcaseClips.map((clip) => (
                  <div
                    key={clip.id}
                    onClick={() => setActiveHubTab('reels')}
                    className="p-3.5 bg-indigo-900/80 rounded-2xl border border-white/10 hover:border-purple-400/80 transition-all duration-300 transform hover:scale-[1.02] hover:-translate-y-1 hover:shadow-[0_0_25px_rgba(168,85,247,0.3)] flex items-center space-x-4 shadow-lg group cursor-pointer relative"
                  >
                    <div className="relative w-28 h-20 rounded-xl overflow-hidden bg-black shrink-0 border border-white/10">
                      <img
                        src={clip.thumbnailUrl || 'https://images.unsplash.com/photo-1612872087720-bb876e2e67d1?auto=format&fit=crop&q=80&w=600'}
                        alt={clip.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition duration-300"
                      />
                      {/* Credential Status Indicator Overlay on Highlight Thumbnail */}
                      <div className="absolute top-1.5 left-1.5 flex items-center space-x-1 bg-black/80 backdrop-blur-md px-1.5 py-0.5 rounded-full border border-emerald-400/60 shadow-md">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                        <span className="text-[8px] font-black uppercase text-emerald-300 tracking-wider">PRO Verified</span>
                      </div>

                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <div className="w-8 h-8 rounded-full bg-lime-400 text-black flex items-center justify-center shadow-lg group-hover:scale-110 transition">
                          <Play className="w-4 h-4 fill-black ml-0.5" />
                        </div>
                      </div>
                      <span className="absolute bottom-1 right-1 bg-black/80 text-white text-[9px] font-mono px-1.5 py-0.5 rounded">
                        0:{clip.durationSeconds || 30}
                      </span>
                    </div>

                    <div className="flex-1 space-y-1">
                      <div className="flex items-center space-x-2">
                        <span className="text-[9px] font-black uppercase px-2 py-0.5 bg-purple-500/20 text-purple-300 rounded-full border border-purple-500/30">
                          {clip.sport || userSport}
                        </span>
                        <span className="text-[10px] text-indigo-300 font-mono">
                          {clip.createdAt}
                        </span>
                      </div>
                      <h3 className="text-xs font-black italic uppercase text-white line-clamp-1 group-hover:text-purple-300 transition">
                        {clip.title}
                      </h3>
                      <div className="flex items-center space-x-3 text-[10px] text-indigo-200 font-mono">
                        <span className="flex items-center space-x-1 text-cyan-300">
                          <Eye className="w-3 h-3" />
                          <span>{clip.views || 100} views</span>
                        </span>
                        <span className="flex items-center space-x-1 text-rose-400">
                          <Heart className="w-3 h-3 fill-rose-400" />
                          <span>{clip.likes || 25} likes</span>
                        </span>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveHubTab('reels');
                      }}
                      className="p-2 bg-indigo-950 hover:bg-lime-400 text-indigo-300 hover:text-black rounded-xl border border-white/10 transition"
                      title="Play in Highlight Theater"
                    >
                      <Play className="w-4 h-4 fill-current" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      )}

      {/* REELS & CHALLENGES SUB-VIEW */}
      {activeHubTab === 'reels' && (
        <HighlightReelView
          user={user}
          allAthletes={allAthletes}
          onOpenUploadModal={onOpenUploadModal}
          onEarnXp={(amount, source) => {
            if (onClaimXp) onClaimXp(amount, source);
          }}
          onUpdateProfile={onUpdateProfile}
        />
      )}

      {/* VOLLEYBALL & SPORT DRILLS SUB-VIEW */}
      {activeHubTab === 'challenges' && (
        <VolleyballChallengesView
          user={user}
          onEarnXp={onClaimXp}
          onUpdateProfile={onUpdateProfile}
        />
      )}

      {/* BADGES & ACHIEVEMENTS MAIN VIEW */}
      {activeHubTab === 'achievements' && (
        <>
          {/* Top Banner Card */}
      <div className="bg-gradient-to-r from-indigo-950 via-indigo-900 to-purple-950 p-6 sm:p-8 rounded-[2.5rem] border border-white/15 shadow-2xl relative overflow-hidden">
        <div className="absolute right-0 top-0 bottom-0 w-1/3 opacity-15 pointer-events-none bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-lime-400 via-amber-400 to-transparent" />

        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-2 max-w-xl">
            <div className="inline-flex items-center space-x-2 bg-lime-400/20 border border-lime-400/30 px-3 py-1 rounded-full text-xs font-black italic uppercase text-lime-300">
              <Trophy className="w-3.5 h-3.5" />
              <span>Athlete Achievements & Milestones</span>
            </div>

            <h2 className="text-2xl sm:text-3xl font-black italic uppercase text-white tracking-tight">
              Unlockable <span className="text-lime-400">Badges & XP</span>
            </h2>

            <p className="text-xs text-indigo-200/80 font-semibold leading-relaxed">
              Track key accomplishments like "First Win", "10 Games Played", and "Century Scorer". Earn bonus XP to level up your athlete profile.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 shrink-0">
            {/* Share Achievement Button */}
            <button
              type="button"
              onClick={() => handleOpenShareModal()}
              className="px-5 py-3.5 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase text-xs rounded-2xl shadow-xl shadow-lime-400/20 transition flex items-center justify-center space-x-2 group shrink-0"
            >
              <Share2 className="w-4 h-4 stroke-[2.5] group-hover:scale-110 transition-transform" />
              <span>Share Achievement Snapshot</span>
            </button>

            {/* Progress Overview Pill */}
            <div className="bg-indigo-950/80 p-5 rounded-2xl border border-white/10 flex items-center space-x-6 shrink-0 shadow-lg">
              <div className="text-center space-y-0.5">
                <span className="text-2xl font-black italic text-lime-400 block">
                  {unlockedCount} / {totalCount}
                </span>
                <span className="text-[10px] font-extrabold text-indigo-300 uppercase tracking-wider block">
                  Badges Unlocked
                </span>
              </div>

              <div className="h-10 w-px bg-white/10" />

              <div className="text-center space-y-0.5">
                <span className="text-2xl font-black italic text-amber-300 block">
                  +{totalXpEarned.toLocaleString()}
                </span>
                <span className="text-[10px] font-extrabold text-indigo-300 uppercase tracking-wider block">
                  Total XP Earned
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Global Progress Bar */}
        <div className="mt-6 pt-4 border-t border-white/10 space-y-1.5">
          <div className="flex justify-between items-center text-xs font-bold text-indigo-200">
            <span>Overall Milestone Progress</span>
            <span className="text-lime-400 font-mono font-black">
              {Math.round((unlockedCount / totalCount) * 100)}% Completed
            </span>
          </div>
          <div className="w-full h-3 bg-indigo-950 rounded-full overflow-hidden border border-white/10">
            <div
              className="h-full bg-gradient-to-r from-lime-400 via-amber-400 to-rose-400 rounded-full transition-all duration-700 shadow-md"
              style={{ width: `${(unlockedCount / totalCount) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* 50-Level Recharts Progress Visualizer (15% logarithmic XP growth curve) */}
      <LevelProgressionRechartsCard user={user} onShowAnnouncement={handleShowAnnouncementForLevel} />

      {/* 30-Day Streak & Consistency Calendar Section */}
      <StreakCalendar user={user} onUpdateProfile={onUpdateProfile} />

      {/* VOLLEYBALL WEEKLY CHALLENGES & DRILLS MODULE */}
      <VolleyballChallengesView
        user={user}
        onEarnXp={(amount, reason) => {
          if (onClaimXp) onClaimXp(amount, reason);
        }}
        onUpdateProfile={onUpdateProfile}
      />

      {/* Filter & Search Bar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 bg-indigo-900/60 p-4 rounded-2xl border border-white/10 shadow-md">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-indigo-300 absolute left-3 top-3" />
          <input
            type="text"
            placeholder="Search milestone or badge name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-indigo-950 border border-white/10 rounded-xl text-xs font-semibold text-white outline-none focus:ring-2 focus:ring-lime-400"
          />
        </div>

        {/* Category & Status Segmented Controls */}
        <div className="flex items-center space-x-2">
          {/* Status filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as any)}
            className="p-2 bg-indigo-950 border border-white/10 rounded-xl text-xs font-bold text-white outline-none focus:ring-2 focus:ring-lime-400"
          >
            <option value="all">All Badges</option>
            <option value="unlocked">Unlocked Only ({unlockedCount})</option>
            <option value="locked">Locked Only ({totalCount - unlockedCount})</option>
          </select>

          {/* Sport Category filter */}
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="p-2 bg-indigo-950 border border-white/10 rounded-xl text-xs font-bold text-white outline-none focus:ring-2 focus:ring-lime-400 capitalize"
          >
            <option value="all">All Categories</option>
            <option value="general">General</option>
            <option value="elite_series">Elite Series 👑 (PRO Verified)</option>
            <option value="volleyball">Volleyball 🏐</option>
            <option value="basketball">Basketball 🏀</option>
            <option value="baseball">Baseball ⚾</option>
            <option value="soccer">Soccer ⚽</option>
          </select>
        </div>
      </div>

      {/* Badges Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredMilestones.map((m) => {
          const isUnlocked = m.currentValue >= m.targetValue;
          const progressPct = Math.min(100, Math.round((m.currentValue / m.targetValue) * 100));
          const tierStyle = getTierBadge(m.tier);
          const isClaimed = claimedBadges[m.id];
          const isProBadge = m.isProVerified || m.tier === 'pro_elite' || m.category === 'elite_series';

          return (
            <motion.div
              key={m.id}
              onClick={() => setSelectedMilestone(m)}
              animate={
                isProBadge && isUnlocked
                  ? {
                      boxShadow: [
                        '0 0 15px rgba(251,191,36,0.3)',
                        '0 0 30px rgba(251,191,36,0.7)',
                        '0 0 15px rgba(251,191,36,0.3)',
                      ],
                    }
                  : undefined
              }
              style={getLegendaryBadgeFilter(m.tier)}
              transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
              className={`group cursor-pointer rounded-3xl p-5 border transition-all duration-300 relative overflow-hidden flex flex-col justify-between ${
                isUnlocked && (m.category === 'elite_series' || m.tier === 'pro_elite' || m.tier === 'legendary' || m.isProVerified)
                  ? 'animate-elite-breathing'
                  : ''
              } ${
                m.tier === 'legendary'
                  ? 'bg-gradient-to-b from-amber-900/90 via-amber-950 to-indigo-950 border-2 border-amber-300'
                  : isProBadge
                  ? isUnlocked
                    ? 'bg-gradient-to-b from-amber-950/90 via-indigo-950 to-indigo-900 border-2 border-amber-400 shadow-xl'
                    : 'bg-indigo-950/70 border-amber-400/30 opacity-80 hover:opacity-100'
                  : isUnlocked
                  ? 'bg-gradient-to-b from-indigo-900/90 to-indigo-950 border-white/20 hover:border-lime-400 shadow-xl'
                  : 'bg-indigo-950/60 border-white/10 opacity-75 hover:opacity-100 hover:border-white/20'
              }`}
            >
              {/* Background Tier Glow */}
              <div
                className={`absolute inset-0 bg-gradient-to-br ${tierStyle.glow} pointer-events-none opacity-30`}
              />

              <div className="space-y-3 relative z-10">
                {/* Header Row: Icon + Badges */}
                <div className="flex items-start justify-between">
                  <div
                    className={`w-12 h-12 rounded-2xl flex items-center justify-center border shadow-lg transition group-hover:scale-110 ${
                      isUnlocked
                        ? 'bg-gradient-to-br from-lime-400 to-emerald-500 text-black border-lime-300'
                        : 'bg-indigo-900 text-indigo-400 border-white/10'
                    }`}
                  >
                    {renderIcon(m.icon, 'w-6 h-6 stroke-[2.5]')}
                  </div>

                  <div className="flex flex-col items-end space-y-1">
                    {/* Tier Tag */}
                    <span
                      className={`text-[9px] font-black italic uppercase px-2 py-0.5 rounded-full border ${tierStyle.bg}`}
                    >
                      {tierStyle.label} Tier
                    </span>

                    {/* XP Tag */}
                    <span className="text-[10px] font-extrabold text-amber-300 bg-amber-400/10 px-2 py-0.5 rounded-md border border-amber-400/20">
                      +{m.xpReward} XP
                    </span>
                  </div>
                </div>

                {/* Milestone Title & Description */}
                <div>
                  <h3 className="font-black italic uppercase text-white text-base flex items-center space-x-1.5">
                    <span>{m.name}</span>
                    {isUnlocked && <CheckCircle2 className="w-4 h-4 text-lime-400 shrink-0" />}
                  </h3>
                  <p className="text-xs text-indigo-200/70 font-semibold mt-1 line-clamp-2">
                    {m.description}
                  </p>
                </div>
              </div>

              {/* Progress Footer */}
              <div className="mt-4 pt-3 border-t border-white/10 space-y-2 relative z-10">
                <div className="flex items-center justify-between text-[11px] font-bold">
                  <span className="text-indigo-300/80">
                    {isUnlocked ? 'Requirement Met' : 'Progress'}
                  </span>
                  <span className="font-mono text-white">
                    {m.currentValue} / {m.targetValue}
                  </span>
                </div>

                {/* Bar */}
                <div className="w-full h-2 bg-indigo-950 rounded-full overflow-hidden border border-white/10">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      isUnlocked ? 'bg-lime-400 shadow-sm shadow-lime-400/50' : 'bg-indigo-700'
                    }`}
                    style={{ width: `${progressPct}%` }}
                  />
                </div>

                {/* Status indicator button or unlock date */}
                <div className="flex items-center justify-between pt-1">
                  {isUnlocked ? (
                    <span className="text-[10px] text-lime-400 font-bold italic flex items-center">
                      <Check className="w-3 h-3 mr-1" />
                      Unlocked {m.unlockedAt ? `(${m.unlockedAt})` : ''}
                    </span>
                  ) : (
                    <span className="text-[10px] text-indigo-400 font-bold italic flex items-center">
                      <Lock className="w-3 h-3 mr-1 text-indigo-400" />
                      Locked ({progressPct}%)
                    </span>
                  )}

                  {isUnlocked && (
                    <div className="flex items-center space-x-1.5">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTogglePin(m.id);
                        }}
                        className={`px-2 py-1.5 rounded-lg border transition flex items-center space-x-1 text-[10px] font-bold ${
                          pinnedBadgeIds.includes(m.id)
                            ? 'bg-amber-400 text-black border-amber-300'
                            : 'bg-indigo-900 hover:bg-amber-400/20 text-amber-300 border-amber-400/30'
                        }`}
                        title={pinnedBadgeIds.includes(m.id) ? 'Unpin from Profile Showcase' : 'Pin to Profile Showcase'}
                      >
                        <Pin className={`w-3 h-3 ${pinnedBadgeIds.includes(m.id) ? 'fill-black' : ''}`} />
                        <span>{pinnedBadgeIds.includes(m.id) ? 'Pinned' : 'Pin'}</span>
                      </button>

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenShareModal(m);
                        }}
                        className="p-1.5 bg-indigo-900 hover:bg-indigo-800 text-lime-300 rounded-lg border border-lime-400/30 transition flex items-center space-x-1 text-[10px] font-bold"
                        title="Generate Share Snapshot Image"
                      >
                        <Share2 className="w-3 h-3 text-lime-400" />
                        <span>Snapshot</span>
                      </button>
                    </div>
                  )}

                  {isUnlocked && !isClaimed && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClaim(m);
                      }}
                      className="px-2.5 py-1 bg-lime-400 hover:bg-lime-300 text-black text-[10px] font-black italic uppercase rounded-lg shadow transition"
                    >
                      Claim XP
                    </button>
                  )}

                  {isClaimed && (
                    <span className="text-[10px] text-lime-300 font-bold bg-lime-400/20 px-2 py-0.5 rounded-md border border-lime-400/30">
                      XP Claimed ✓
                    </span>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {filteredMilestones.length === 0 && (
        <div className="p-12 text-center bg-indigo-900/40 rounded-[2.5rem] border border-dashed border-white/10 space-y-2">
          <Trophy className="w-10 h-10 text-indigo-300/50 mx-auto" />
          <p className="text-sm font-bold text-indigo-200">No achievements found matching your search.</p>
          <button
            onClick={() => {
              setSearchQuery('');
              setFilterCategory('all');
              setFilterStatus('all');
            }}
            className="text-xs font-black italic uppercase text-lime-400 hover:underline"
          >
            Reset Filters
          </button>
        </div>
      )}

      {/* Achievement Detail Modal */}
      {selectedMilestone && (
        <div className="fixed inset-0 z-50 bg-indigo-950/85 backdrop-blur-md flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-indigo-900 border border-white/15 rounded-[2.5rem] p-6 max-w-md w-full text-white space-y-5 relative shadow-2xl">
            <button
              onClick={() => setSelectedMilestone(null)}
              className="absolute top-5 right-5 p-2 rounded-full bg-indigo-950 text-indigo-200 hover:text-white border border-white/10"
            >
              ✕
            </button>

            <div className="flex items-center space-x-4">
              <div
                className={`w-16 h-16 rounded-2xl flex items-center justify-center border shadow-xl ${
                  selectedMilestone.currentValue >= selectedMilestone.targetValue
                    ? 'bg-gradient-to-br from-lime-400 to-emerald-500 text-black border-lime-300'
                    : 'bg-indigo-950 text-indigo-300 border-white/10'
                }`}
              >
                {renderIcon(selectedMilestone.icon, 'w-8 h-8 stroke-[2.5]')}
              </div>

              <div className="space-y-1">
                <div className="flex items-center space-x-2">
                  <span className="text-[10px] font-black italic uppercase px-2 py-0.5 rounded-full bg-indigo-950 border border-white/10 text-indigo-300">
                    {selectedMilestone.category}
                  </span>
                  <span className="text-[10px] font-extrabold text-amber-300">
                    +{selectedMilestone.xpReward} XP
                  </span>
                </div>
                <h3 className="text-xl font-black italic uppercase text-white">
                  {selectedMilestone.name}
                </h3>
              </div>
            </div>

            <div className="space-y-3 bg-indigo-950 p-4 rounded-2xl border border-white/10 text-xs">
              <div>
                <span className="text-indigo-300/70 font-bold uppercase text-[10px] block">Description</span>
                <p className="text-white font-semibold mt-0.5">{selectedMilestone.description}</p>
              </div>

              <div>
                <span className="text-indigo-300/70 font-bold uppercase text-[10px] block">How to Unlock</span>
                <p className="text-lime-300 font-semibold mt-0.5">{selectedMilestone.tips || 'Log stats or participate in playground matches.'}</p>
              </div>

              <div className="pt-2 border-t border-white/10 flex items-center justify-between">
                <span className="text-indigo-200/80 font-bold">Requirement Target:</span>
                <span className="font-mono text-white font-extrabold">
                  {selectedMilestone.currentValue} / {selectedMilestone.targetValue}
                </span>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  const m = selectedMilestone;
                  setSelectedMilestone(null);
                  handleOpenShareModal(m);
                }}
                className="flex-1 py-3 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase text-xs rounded-xl shadow-lg transition flex items-center justify-center space-x-1.5"
              >
                <Share2 className="w-4 h-4 stroke-[2.5]" />
                <span>Share Snapshot</span>
              </button>

              <button
                onClick={() => setSelectedMilestone(null)}
                className="px-5 py-3 bg-indigo-950 hover:bg-indigo-800 text-white font-bold text-xs uppercase italic rounded-xl border border-white/10 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SHARE ACHIEVEMENT SNAPSHOT MODAL */}
      {isShareModalOpen && shareMilestone && (
        <div className="fixed inset-0 z-50 bg-indigo-950/90 backdrop-blur-md flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-indigo-900/95 border border-white/15 rounded-[2.5rem] p-6 sm:p-8 max-w-2xl w-full text-white space-y-6 relative shadow-2xl overflow-y-auto max-h-[92vh]">
            {/* Header */}
            <div className="flex items-start justify-between border-b border-white/10 pb-4">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-lime-400 text-black rounded-2xl shadow-lg">
                  <ImageIcon className="w-6 h-6 stroke-[2.5]" />
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 bg-lime-400/20 text-lime-300 rounded-full border border-lime-400/30">
                      THEME: {(activePalette || 'midnight_chrome').replace('_', ' ').toUpperCase()} 🎨
                    </span>
                  </div>
                  <h3 className="text-xl font-black italic uppercase text-white tracking-tight mt-0.5">
                    Achievement Snapshot Generator
                  </h3>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setIsShareModalOpen(false)}
                className="p-2 rounded-full bg-indigo-950 text-indigo-300 hover:text-white border border-white/10 transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Badge Selection Dropdown */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-indigo-200 block">
                Select Badge / Milestone to Feature on Card:
              </label>
              <select
                value={shareMilestone.id}
                onChange={(e) => {
                  const selected = milestonesList.find((m) => m.id === e.target.value);
                  if (selected) setShareMilestone(selected);
                }}
                className="w-full p-3 bg-indigo-950 border border-white/15 rounded-xl text-xs font-black text-white outline-none focus:ring-2 focus:ring-lime-400"
              >
                {milestonesList.map((m) => {
                  const unlocked = m.currentValue >= m.targetValue;
                  return (
                    <option key={m.id} value={m.id}>
                      {unlocked ? '🏆 UNLOCKED' : '🔒 LOCKED'} • {m.name} ({m.tier.toUpperCase()} TIER - +{m.xpReward} XP)
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Live Pre-formatted Snapshot Preview Graphic */}
            <div className="space-y-2">
              <span className="text-xs font-bold text-indigo-300 flex items-center justify-between">
                <span>Pre-formatted Snapshot Preview:</span>
                <span className="text-[10px] text-lime-300 font-mono">1200 x 675 High-Res PNG</span>
              </span>

              <div className="p-3 bg-indigo-950/80 rounded-2xl border border-white/10 shadow-inner flex items-center justify-center overflow-hidden">
                <img
                  src={generateSnapshotDataUrl(shareMilestone)}
                  alt="Achievement Snapshot Preview"
                  className="w-full h-auto max-h-[340px] rounded-xl object-contain shadow-2xl border border-white/10"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3 pt-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Download PNG Button */}
                <a
                  href={generateSnapshotDataUrl(shareMilestone)}
                  download={`${user.handle}_${shareMilestone.id}_snapshot.png`}
                  onClick={() => {
                    setSnapshotToastMsg('Snapshot image downloaded! Ready to share on social media. 📸');
                    setTimeout(() => setSnapshotToastMsg(null), 4000);
                  }}
                  className="py-3.5 px-4 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase text-xs rounded-2xl shadow-xl shadow-lime-400/20 transition flex items-center justify-center space-x-2 text-center"
                >
                  <Download className="w-4 h-4 stroke-[2.5]" />
                  <span>Download Snapshot Image (PNG)</span>
                </a>

                {/* Copy Social Share Text Button */}
                <button
                  type="button"
                  onClick={() => {
                    const text = `🏆 Unlocked the "${shareMilestone.name}" badge on Playgrounds! Level ${user.level} Athlete • +${shareMilestone.xpReward} XP Earned. Check out my athlete profile on Playgrounds!`;
                    navigator.clipboard.writeText(text);
                    setSnapshotToastMsg('Social share text copied to clipboard! 📋');
                    setTimeout(() => setSnapshotToastMsg(null), 4000);
                  }}
                  className="py-3.5 px-4 bg-indigo-950 hover:bg-indigo-800 text-lime-300 border border-lime-400/40 text-xs font-black italic uppercase rounded-2xl transition flex items-center justify-center space-x-2"
                >
                  <Copy className="w-4 h-4" />
                  <span>Copy Social Post Text</span>
                </button>
              </div>

              {snapshotToastMsg && (
                <div className="p-3 bg-lime-400/20 border border-lime-400/40 rounded-xl text-center text-xs font-bold text-lime-300 animate-fadeIn">
                  {snapshotToastMsg}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ELITE CHALLENGE UI ANNOUNCEMENT MODAL */}
      {announcementModal?.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fadeIn">
          <div className="bg-gradient-to-br from-indigo-950 via-indigo-900 to-amber-950 p-6 sm:p-8 rounded-[2.5rem] border-2 border-amber-400 shadow-[0_0_50px_rgba(251,191,36,0.6)] max-w-lg w-full space-y-6 relative overflow-hidden">
            <button
              type="button"
              onClick={() => setAnnouncementModal(null)}
              className="absolute top-5 right-5 p-2 bg-indigo-950 hover:bg-indigo-900 text-white rounded-full border border-white/20 transition"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="text-center space-y-2">
              <div className="inline-flex items-center space-x-2 px-3 py-1 bg-amber-400 text-black text-xs font-black italic uppercase rounded-full font-mono shadow-lg">
                <Crown className="w-4 h-4 text-black" />
                <span>ELITE CHALLENGE ANNOUNCEMENT ⚡</span>
              </div>
              <h2 className="text-3xl font-black italic uppercase text-white tracking-tight">
                LEVEL {announcementModal.level} MILESTONE REACHED!
              </h2>
              <p className="text-xs text-amber-300 font-bold font-mono">
                Official Tier Milestone Unlocked • Athlete Level Progression
              </p>
            </div>

            <div className="bg-indigo-950/80 p-5 rounded-2xl border border-amber-400/40 space-y-3 relative">
              <div className="flex items-center space-x-3">
                <div className="p-3 rounded-2xl bg-amber-400/20 text-amber-300 border border-amber-400/60 shadow-inner">
                  <Sparkles className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-base font-black italic uppercase text-white">
                    {announcementModal.challengeTitle}
                  </h3>
                  <span className="text-xs font-mono text-lime-400 font-extrabold">
                    Reward: +{announcementModal.rewardXp.toLocaleString()} XP
                  </span>
                </div>
              </div>

              <p className="text-xs text-indigo-200/90 italic leading-relaxed">
                {announcementModal.description}
              </p>
            </div>

            <div className="flex items-center justify-between pt-2">
              <button
                type="button"
                onClick={() => {
                  fireEliteSeriesConfetti();
                  setAnnouncementModal(null);
                }}
                className="w-full py-3.5 bg-gradient-to-r from-amber-400 to-yellow-300 hover:from-amber-300 hover:to-yellow-200 text-black font-black italic uppercase text-xs rounded-2xl shadow-xl shadow-amber-400/30 transition text-center flex items-center justify-center space-x-2"
              >
                <Sparkles className="w-4 h-4" />
                <span>Accept Challenge & Blast Confetti</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* STICKY BATCH ACTIONS BAR */}
      {selectedBadgeIds.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-gradient-to-r from-indigo-950 via-purple-950 to-indigo-900 border-2 border-lime-400 p-3 sm:px-6 rounded-3xl shadow-[0_15px_40px_rgba(0,0,0,0.85)] flex flex-wrap items-center justify-between gap-4 max-w-xl w-[92vw] animate-slideUp">
          <div className="flex items-center space-x-2.5">
            <span className="w-3 h-3 rounded-full bg-lime-400 animate-ping shrink-0" />
            <span className="text-xs font-black italic uppercase text-white font-mono tracking-wider">
              {selectedBadgeIds.length} Badge{selectedBadgeIds.length > 1 ? 's' : ''} Selected
            </span>
          </div>

          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={handleBatchPinToFeatured}
              className="px-3.5 py-2 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase text-xs rounded-xl shadow-lg transition flex items-center space-x-1.5 shrink-0"
            >
              <Pin className="w-3.5 h-3.5 fill-black" />
              <span>Pin to Featured</span>
            </button>

            <button
              type="button"
              onClick={handleBatchShareSelection}
              className="px-3.5 py-2 bg-indigo-900 hover:bg-indigo-800 text-amber-300 border border-amber-400/40 font-black italic uppercase text-xs rounded-xl shadow-lg transition flex items-center space-x-1.5 shrink-0"
            >
              <Share2 className="w-3.5 h-3.5" />
              <span>Share Selection</span>
            </button>

            <button
              type="button"
              onClick={handleDeselectAllBadges}
              className="p-2 bg-indigo-950 hover:bg-rose-900/80 text-rose-300 rounded-xl border border-rose-500/30 transition shrink-0"
              title="Clear Selection"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* BADGE QUICK PREVIEW MODAL */}
      {previewBadge && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md animate-fadeIn">
          <div className="bg-gradient-to-br from-indigo-950 via-purple-950 to-slate-950 p-6 sm:p-8 rounded-[2.5rem] border-2 border-amber-400 shadow-[0_0_60px_rgba(251,191,36,0.5)] max-w-lg w-full space-y-6 relative overflow-hidden">
            <button
              type="button"
              onClick={() => setPreviewBadge(null)}
              className="absolute top-5 right-5 p-2 bg-indigo-950 hover:bg-indigo-900 text-white rounded-full border border-white/20 transition z-10"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Header Tag */}
            <div className="text-center space-y-2">
              <div className="inline-flex items-center space-x-2 px-3.5 py-1 bg-amber-400/20 border border-amber-400/50 text-amber-300 text-xs font-black italic uppercase rounded-full font-mono">
                <Sparkles className="w-4 h-4 text-amber-400" />
                <span>BADGE QUICK PREVIEW • {previewBadge.tier.toUpperCase()} TIER</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-black italic uppercase text-white tracking-tight">
                {previewBadge.name}
              </h2>
              <p className="text-xs text-lime-400 font-black font-mono">
                +{previewBadge.xpReward} XP Reward Career Boost
              </p>
            </div>

            {/* Zoomed-In Emblem Card */}
            <div className="relative p-6 bg-indigo-900/60 rounded-3xl border border-amber-400/40 text-center space-y-4 overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-amber-400/15 via-transparent to-transparent pointer-events-none" />
              
              {/* Large Icon Box */}
              <div className="relative mx-auto w-24 h-24 rounded-3xl bg-gradient-to-br from-amber-400 via-yellow-300 to-lime-400 text-black flex items-center justify-center shadow-2xl ring-4 ring-amber-400/50 transform hover:scale-105 transition-transform duration-300">
                {renderIcon(previewBadge.icon, 'w-12 h-12 stroke-[2.5]')}
                {/* Credential Status Indicator */}
                <div className="absolute -top-2 -right-2 bg-indigo-950 p-1.5 rounded-full border-2 border-emerald-400 shadow-xl flex items-center justify-center" title="Verified Active Badge">
                  <span className="w-3 h-3 rounded-full bg-emerald-400 animate-pulse" />
                </div>
              </div>

              <p className="text-xs text-indigo-200 font-semibold leading-relaxed max-w-sm mx-auto">
                {previewBadge.description}
              </p>
            </div>

            {/* Specific Earning Criteria & Game Details */}
            <div className="bg-indigo-950/90 p-4 sm:p-5 rounded-2xl border border-white/10 space-y-3">
              <div className="flex items-center space-x-2 border-b border-white/10 pb-2.5">
                <Trophy className="w-4 h-4 text-amber-400" />
                <h4 className="text-xs font-black italic uppercase text-white tracking-wider">
                  Award Criteria & Match Context
                </h4>
              </div>

              <div className="space-y-2 text-xs font-mono">
                <div className="flex items-start justify-between text-indigo-200">
                  <span className="text-indigo-400">Status:</span>
                  <span className="font-bold text-lime-400">
                    {previewBadge.currentValue >= previewBadge.targetValue ? 'VERIFIED EARNED AWARD ✓' : 'IN PROGRESS'}
                  </span>
                </div>

                <div className="flex items-start justify-between text-indigo-200">
                  <span className="text-indigo-400">Earned Date:</span>
                  <span className="font-bold text-amber-300">
                    {previewBadge.unlockedAt || 'Aug 5, 2026 (Recent League Run)'}
                  </span>
                </div>

                <div className="flex items-start justify-between text-indigo-200">
                  <span className="text-indigo-400">Match Location:</span>
                  <span className="font-bold text-cyan-300">
                    Mater Dei High School Athletic Center
                  </span>
                </div>

                <div className="flex items-start justify-between text-indigo-200">
                  <span className="text-indigo-400">Criteria Goal:</span>
                  <span className="font-bold text-white">
                    {previewBadge.targetValue} {previewBadge.name} ({previewBadge.currentValue}/{previewBadge.targetValue} Achieved)
                  </span>
                </div>

                {previewBadge.tips && (
                  <p className="text-[11px] text-amber-200/90 bg-amber-400/10 p-2.5 rounded-xl border border-amber-400/30 italic">
                    💡 Tip: {previewBadge.tips}
                  </p>
                )}
              </div>
            </div>

            {/* Modal Action Buttons */}
            <div className="flex items-center justify-between gap-3 pt-2">
              <button
                type="button"
                onClick={() => {
                  handleTogglePin(previewBadge.id);
                }}
                className={`flex-1 py-3 px-4 text-xs font-black italic uppercase rounded-2xl border transition flex items-center justify-center space-x-2 ${
                  pinnedBadgeIds.includes(previewBadge.id)
                    ? 'bg-amber-400/20 text-amber-300 border-amber-400/60'
                    : 'bg-indigo-900 text-white border-white/20 hover:bg-indigo-800'
                }`}
              >
                <Pin className="w-4 h-4 fill-current" />
                <span>{pinnedBadgeIds.includes(previewBadge.id) ? 'Pinned to Featured' : 'Pin to Featured'}</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  handleOpenShareModal(previewBadge);
                  setPreviewBadge(null);
                }}
                className="flex-1 py-3 px-4 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase text-xs rounded-2xl shadow-lg transition flex items-center justify-center space-x-2"
              >
                <Share2 className="w-4 h-4 stroke-[2.5]" />
                <span>Snapshot</span>
              </button>
            </div>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
};

export const LevelProgressionRechartsCard: React.FC<{
  user: AthleteProfile;
  onShowAnnouncement?: (lvl: number) => void;
}> = ({ user, onShowAnnouncement }) => {
  const levelInfo = getLevelInfo(user.xp || 0);

  // Generate data for all 50 levels showing the 15% logarithmic/exponential XP curve
  const chartData = Array.from({ length: 50 }, (_, i) => {
    const lvl = i + 1;
    const cumXp = getCumulativeXpForLevel(lvl);
    const gapXp = Math.round(500 * Math.pow(1.15, i));
    const isCurrent = lvl === levelInfo.level;
    const isMilestone = lvl === 10 || lvl === 25 || lvl === 50;
    return {
      level: `L${lvl}`,
      lvlNum: lvl,
      cumXp,
      gapXp,
      isCurrent,
      isMilestone,
    };
  });

  return (
    <div className="bg-gradient-to-br from-indigo-950 via-indigo-900 to-amber-950/50 p-6 rounded-[2.5rem] border-2 border-amber-400/50 shadow-2xl space-y-4">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 border-b border-white/10 pb-4">
        <div>
          <div className="flex items-center space-x-2">
            <span className="px-2.5 py-0.5 bg-amber-400 text-black text-[10px] font-black uppercase font-mono rounded">
              50-LEVEL PROGRESSION ENGINE ⚡
            </span>
            <span className="text-xs text-amber-300 font-bold">15% Compound Scaling Growth Curve</span>
          </div>
          <h3 className="text-xl font-black italic uppercase text-white mt-1 flex items-center space-x-2">
            <span>Level {levelInfo.level}: {levelInfo.levelTitle}</span>
            {levelInfo.isMilestoneLevel && (
              <span className="bg-amber-400/20 text-amber-300 text-xs px-2 py-0.5 rounded border border-amber-400/40 font-mono">
                MILESTONE TIER {levelInfo.milestoneLevelTier} 👑
              </span>
            )}
          </h3>
        </div>

        <div className="flex items-center space-x-3 bg-indigo-950/80 p-3 rounded-2xl border border-amber-400/30">
          <div className="text-right font-mono">
            <span className="text-[10px] text-indigo-300 uppercase block">Next Level XP Gap</span>
            <span className="text-sm font-black text-lime-400">
              {levelInfo.xpInCurrentLevel.toLocaleString()} / {levelInfo.xpRequiredForNextLevel.toLocaleString()} XP
            </span>
          </div>
          <div className="w-12 h-12 rounded-xl bg-amber-400/20 border border-amber-400/60 flex items-center justify-center font-black text-amber-300 text-lg">
            {levelInfo.progressPct}%
          </div>
        </div>
      </div>

      {/* Recharts Area Chart showing the 50 Levels XP Curve */}
      <div className="h-56 w-full pt-2">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="xpCurveGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#6366F1" stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#312E81" opacity={0.3} />
            <XAxis dataKey="level" stroke="#A5B4FC" fontSize={10} interval={4} />
            <YAxis stroke="#A5B4FC" fontSize={10} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
            <Tooltip
              contentStyle={{
                backgroundColor: '#0B0F2A',
                borderColor: '#F59E0B',
                borderRadius: '16px',
                color: '#fff',
                fontSize: '12px',
              }}
              formatter={(val: any, name: any) => [
                `${Number(val).toLocaleString()} XP`,
                name === 'cumXp' ? 'Cumulative XP Required' : 'Level Gap XP (+15%)',
              ]}
            />
            <Area
              type="monotone"
              dataKey="cumXp"
              name="cumXp"
              stroke="#F59E0B"
              strokeWidth={3}
              fillOpacity={1}
              fill="url(#xpCurveGrad)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Major Milestone Circular Progress Indicators (Levels 10, 25, 50) */}
      <div className="pt-3 border-t border-white/10 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Crown className="w-4 h-4 text-amber-400" />
            <h4 className="text-xs font-black italic uppercase text-white font-mono">
              Milestone Circular Progress Rings (Levels 10 • 25 • 50)
            </h4>
          </div>
          <span className="text-[10px] text-amber-300 font-bold font-mono">
            Elite Challenge Thresholds 👑
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            {
              mTier: 10,
              title: 'Level 10: Playground Starter',
              badgeIcon: '🔥',
              challengeName: 'First 10 Games & 100 PTS',
              rewardXp: 1500,
              color: '#38BDF8',
            },
            {
              mTier: 25,
              title: 'Level 25: Court General',
              badgeIcon: '⚡',
              challengeName: '500 PTS & 25 Win Streak',
              rewardXp: 3500,
              color: '#F59E0B',
            },
            {
              mTier: 50,
              title: 'Level 50: Immortal GOAT',
              badgeIcon: '👑',
              challengeName: '1,000 PTS & League MVP',
              rewardXp: 10000,
              color: '#A855F7',
            },
          ].map(({ mTier, title, badgeIcon, challengeName, rewardXp, color }) => {
            const reqXp = getCumulativeXpForLevel(mTier);
            const userXp = user.xp || 0;
            const isCompleted = levelInfo.level >= mTier;
            const progressPct = isCompleted
              ? 100
              : Math.min(99, Math.round((userXp / reqXp) * 100));

            const size = 64;
            const strokeWidth = 6;
            const radius = (size - strokeWidth) / 2;
            const circumference = 2 * Math.PI * radius;
            const strokeDashoffset = circumference - (progressPct / 100) * circumference;

            return (
              <div
                key={mTier}
                className={`p-4 rounded-2xl border transition-all duration-300 flex items-center justify-between gap-3 ${
                  isCompleted
                    ? 'bg-amber-950/40 border-amber-400/60 shadow-lg shadow-amber-400/10'
                    : 'bg-indigo-950/80 border-white/10 hover:border-white/20'
                }`}
              >
                <div className="space-y-1 flex-1">
                  <div className="flex items-center space-x-1.5">
                    <span className="text-sm">{badgeIcon}</span>
                    <h5 className="text-xs font-black italic uppercase text-white tracking-tight">
                      {title}
                    </h5>
                  </div>
                  <p className="text-[10px] text-indigo-200/70 font-mono">
                    {challengeName}
                  </p>
                  <div className="pt-1 flex items-center justify-between text-[9px] font-mono">
                    <span className="text-lime-400 font-bold">+{rewardXp.toLocaleString()} XP</span>
                    <span className={isCompleted ? 'text-amber-300 font-black' : 'text-indigo-400'}>
                      {isCompleted ? 'UNLOCKED ✓' : `${userXp.toLocaleString()} / ${reqXp.toLocaleString()} XP`}
                    </span>
                  </div>
                </div>

                {/* Circular SVG Ring */}
                <div className="relative shrink-0 flex items-center justify-center" style={{ width: size, height: size }}>
                  <svg width={size} height={size} className="transform -rotate-90">
                    <circle
                      cx={size / 2}
                      cy={size / 2}
                      r={radius}
                      stroke="#1E1B4B"
                      strokeWidth={strokeWidth}
                      fill="transparent"
                    />
                    <circle
                      cx={size / 2}
                      cy={size / 2}
                      r={radius}
                      stroke={isCompleted ? '#F59E0B' : color}
                      strokeWidth={strokeWidth}
                      fill="transparent"
                      strokeDasharray={circumference}
                      strokeDashoffset={strokeDashoffset}
                      strokeLinecap="round"
                      className="transition-all duration-700 ease-out"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                    <span className="text-[11px] font-black font-mono text-white leading-none">
                      {progressPct}%
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Milestone Quick Jump Buttons for Lvl 10, 25, 50 */}
      <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-white/10 text-xs font-mono">
        <span className="text-indigo-300 font-bold uppercase text-[10px]">Major Milestone Tiers:</span>
        <div className="flex items-center space-x-2">
          {[10, 25, 50].map((mTier) => (
            <button
              key={mTier}
              type="button"
              onClick={() => onShowAnnouncement && onShowAnnouncement(mTier)}
              className={`px-2.5 py-1 rounded-lg text-[11px] font-black uppercase transition border flex items-center space-x-1 ${
                levelInfo.level >= mTier
                  ? 'bg-amber-400/20 text-amber-300 border-amber-400/50 hover:bg-amber-400/30'
                  : 'bg-indigo-950 text-indigo-400 border-white/10 hover:text-white'
              }`}
            >
              <Crown className="w-3 h-3 text-amber-400" />
              <span>Level {mTier} Challenge</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
